import { useState, useEffect } from 'react';
import api from '../api';
import {
  Gift,
  Plus,
  Trash2,
  RefreshCw,
  Edit2,
  Save,
  X,
  Eye,
  EyeOff,
  Users,
  UserCheck,
  UserPlus,
  Clock,
  Calendar,
  Target,
  ChevronDown,
  ChevronRight,
  Award,
  CheckCircle,
  AlertCircle,
  Link,
  Search,
  Sparkles,
  Crown,
  Star,
  Cake,
  Trophy,
  Shield,
  TrendingUp,
  Percent,
  Zap,
  Send,
  Hash,
  Copy,
  Check,
  Bell,
  Archive,
} from 'lucide-react';
import toast from 'react-hot-toast';
import WinbackAutomationPanel from '../components/WinbackAutomationPanel';
import ExclusionListModal from '../components/ExclusionListModal';

// Helper: Convert UTC ISO string from API to local datetime-local format for input display
const utcToLocal = (utcStr) => {
  if (!utcStr) return '';
  const d = new Date(utcStr);
  if (isNaN(d.getTime())) return '';
  // Offset to local time and format as YYYY-MM-DDTHH:MM
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

// Helper: Convert local datetime-local value to UTC ISO string for API submission
const localToUtc = (localStr) => {
  if (!localStr) return null;
  // datetime-local gives "YYYY-MM-DDTHH:MM" in local time
  // new Date() interprets this as local time automatically
  const d = new Date(localStr);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

const ICONS = [
  'gift', 'star', 'trophy', 'medal', 'diamond', 'heart', 'crown',
  'ticket', 'percent', 'dollar-sign', 'coffee', 'utensils', 'beer',
  'shopping-bag', 'tag', 'zap', 'flame', 'sparkles',
];

const USAGE_TYPES = [
  { value: 'single', label: 'Single Use' },
  { value: 'multiple', label: 'Multiple Uses' },
  { value: 'unlimited', label: 'Unlimited' },
];

const VALUE_TYPES = [
  { value: 'dollar', label: 'Dollar Amount ($)' },
  { value: 'percent', label: 'Percentage (%)' },
];

const TARGET_TYPES = [
  { value: 'all', label: 'All Players' },
  { value: 'users', label: 'Specific Users' },
  { value: 'groups', label: 'User Groups' },
  { value: 'activity', label: 'Recent Play (Hours/Days)' },
];

const TRIGGER_TYPES = [
  { value: 'none', label: 'No Trigger (Manual)', category: 'manual' },
  // Activity-based triggers
  { value: 'sessions', label: 'Sessions Played', category: 'activity', needsValue: true },
  { value: 'hours', label: 'Hours Played', category: 'activity', needsValue: true },
  { value: 'points', label: 'Points Earned', category: 'activity', needsValue: true },
  { value: 'games_played', label: 'Games Played', category: 'activity', needsValue: true },
  { value: 'consecutive_days', label: 'Consecutive Days', category: 'activity', needsValue: true },
  { value: 'first_session', label: 'First Session', category: 'activity' },
  // Tier-based triggers
  { value: 'tier_achieved', label: 'Tier Achieved (One-time)', category: 'tier', needsTier: true },
  { value: 'tier_current', label: 'Current Tier (Ongoing)', category: 'tier', needsTier: true },
  { value: 'level_achieved', label: 'Level Achieved (One-time)', category: 'tier', needsLevel: true },
  { value: 'level_current', label: 'Current Level (Ongoing)', category: 'tier', needsLevel: true },
  { value: 'tier_renewed', label: 'Tier Renewed', category: 'tier' },
  // Achievement trigger
  { value: 'achievement_unlocked', label: 'Achievement Unlocked', category: 'achievement', needsAchievement: true },
  // Account lifecycle triggers
  { value: 'signup', label: 'On Signup', category: 'lifecycle' },
  { value: 'player_club_linked', label: 'Player\'s Club Linked', category: 'lifecycle' },
  { value: 'birthday', label: 'Birthday', category: 'lifecycle' },
  { value: 'review_confirmed', label: 'Google Review Confirmed', category: 'lifecycle' },
  // Referral triggers
  { value: 'referral_made', label: 'Referral Made (Referrer)', category: 'referral' },
  { value: 'referral_session', label: 'Referral Played Session (Referrer)', category: 'referral' },
  { value: 'referral_received', label: 'Referral Received (New User)', category: 'referral' },
];

const DEFAULT_TIER_OPTIONS = [
  { value: 'vip', label: 'VIP' },
  { value: 'elite', label: 'Elite' },
  { value: 'ambassador', label: 'Ambassador' },
  { value: 'chairman', label: 'Chairman' },
  { value: 'legend', label: 'Legend' },
];

const LEVEL_OPTIONS = Array.from({ length: 13 }, (_, i) => ({
  value: i + 1,
  label: `Level ${i + 1}`,
}));

const CATEGORIES = [
  'Food & Beverage',
  'Merchandise',
  'Free Play',
  'Match Play',
  'Special Access',
  'Promotional',
  'XP Boost',
  'Other',
];

const REWARD_TYPES = [
  { value: 'standard', label: 'Standard Reward' },
  { value: 'xp_multiplier', label: 'XP Multiplier' },
];

const MULTIPLIER_APPLIES_TO_OPTIONS = [
  { value: 'all', label: 'All XP Sources' },
  { value: 'all_games', label: 'All Games (Poker + Table Games)' },
  { value: 'poker', label: 'Poker Only' },
  { value: 'cal_games', label: 'Table Games Only' },
  { value: 'visits', label: 'Visit Bonus Only' },
  { value: 'extended_play', label: 'Extended Play Only' },
];

const DURATION_PRESETS = [
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours (1 day)' },
  { value: 48, label: '48 hours (2 days)' },
  { value: 72, label: '72 hours (3 days)' },
  { value: 168, label: '168 hours (1 week)' },
];

const RECURRENCE_TYPES = [
  { value: 'none', label: 'One-time (No Recurrence)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function Rewards() {
  const [rewards, setRewards] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('active'); // active, inactive, all
  const [searchQuery, setSearchQuery] = useState(''); // search filter for rewards list
  
  // Tier configuration from status system
  const [tierOptions, setTierOptions] = useState(DEFAULT_TIER_OPTIONS);
  
  // User search state for targeting
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Quick Send state
  const [showQuickSend, setShowQuickSend] = useState(false);
  // Bulk-deactivate old rewards (scrub Quick Sends)
  const [showBulkDeactivate, setShowBulkDeactivate] = useState(false);
  const [bulkDays, setBulkDays] = useState('30');
  const [bulkQuickSendOnly, setBulkQuickSendOnly] = useState(true);
  const [bulkPreview, setBulkPreview] = useState(null); // { matched }
  const [bulkBusy, setBulkBusy] = useState(false);
  // Reward exclusion-list manager modal
  const [showExclusionList, setShowExclusionList] = useState(false);
  // Daily-limit pools — named groups of categories sharing one redemption/day.
  const [showDailyLimits, setShowDailyLimits] = useState(false);
  const [showSignupHighlight, setShowSignupHighlight] = useState(false);
  const [signupHighlightId, setSignupHighlightId] = useState('');
  const [signupHighlightSaving, setSignupHighlightSaving] = useState(false);
  const [dailyLimitPools, setDailyLimitPools] = useState([]);
  const [dailyLimitsSaving, setDailyLimitsSaving] = useState(false);
  // Saved pools, loaded on mount, used to badge each reward row with its pool.
  const [dailyPools, setDailyPools] = useState([]);
  const [quickSendForm, setQuickSendForm] = useState({
    title: '',
    description: '',
    rewardValue: '',
    rewardValueType: 'dollar',
    category: '',
    icon: 'gift',
    requiresStaffAuth: true,
    expiresAfterDays: '',
    expiresUnit: 'days', expiresAtExact: '',
    sendNotification: true,
    notificationMessage: '',
    recipientMode: 'users',
    tierFilter: '',
    scheduledFor: null,
    // Skip everyone on the global reward exclusion list.
    excludeList: false,
    // Activity filter (By Recent Play)
    activityGames: ['poker', 'cal_games'],
    activityMinHours: '',
    activityDaysWindow: '',
    activityMatchMode: 'hours',
    activityMinPercent: '',
    activityScope: 'recent',
  });
  const [qsUserSearch, setQsUserSearch] = useState('');
  const [qsUserResults, setQsUserResults] = useState([]);
  const [qsSelectedUsers, setQsSelectedUsers] = useState([]);
  const [qsSearching, setQsSearching] = useState(false);
  const [qsSelectedGroups, setQsSelectedGroups] = useState([]);
  const [qsPastedNumbers, setQsPastedNumbers] = useState('');
  const [qsSending, setQsSending] = useState(false);
  // Member numbers from the last send that matched no existing account
  // (so we can surface them instead of silently dropping them).
  const [qsUnmatched, setQsUnmatched] = useState([]);
  const [qsAck, setQsAck] = useState(null); // pending (no-account) recipients — require acknowledgment
  // Recipients skipped because they're on the reward exclusion list.
  const [qsExcluded, setQsExcluded] = useState([]);
  // Activity filter preview state
  const [qsActivityPreview, setQsActivityPreview] = useState(null); // { matched, candidates }
  const [qsActivityPreviewLoading, setQsActivityPreviewLoading] = useState(false);
  // Preview state for the regular reward create/edit form's Recent Play targeting
  const [activityPreview, setActivityPreview] = useState(null); // { matched, candidates }
  const [activityPreviewLoading, setActivityPreviewLoading] = useState(false);

  // Pending assignments state
  const [showPending, setShowPending] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);

  // Notify Recipients modal state
  const [showNotifyRecipients, setShowNotifyRecipients] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ title: '', body: '' });
  const [notifyPrimaryRewardId, setNotifyPrimaryRewardId] = useState(null);
  const [notifySelectedRewardIds, setNotifySelectedRewardIds] = useState([]);
  const [notifyPreview, setNotifyPreview] = useState(null); // { userCount, perReward: [{ rewardId, title, userCount }] }
  const [notifyPreviewLoading, setNotifyPreviewLoading] = useState(false);
  const [notifySending, setNotifySending] = useState(false);

  const [form, setForm] = useState({
    title: '',
    titleEs: '',
    description: '',
    descriptionEs: '',
    icon: 'gift',
    imageUrl: '',
    category: '',
    rewardValue: '',
    rewardValueType: 'dollar',
    redemptionInstructions: '',
    redemptionInstructionsEs: '',
    requiresStaffAuth: true,
    usageType: 'single',
    usageLimit: '',
    dailyExclusive: false,
    isActive: true,
    isHidden: false,
    visibleFrom: '',
    visibleUntil: '',
    expiresAt: '',
    expiresAfterDays: '',
    targetType: 'all',
    triggerType: 'none',
    triggerValue: '',
    // New trigger configuration fields
    triggerTier: '',
    triggerLevel: '',
    triggerAchievementId: '',
    // Multiple trigger conditions (new)
    triggerConditions: [],
    triggerLogic: 'AND',
    useMultipleTriggers: false,
    // Eligibility conditions
    requirePlayerClubLink: false,
    requireMinTier: '',
    requireMinLevel: '',
    requireVerifiedEmail: false,
    requireMinSessions: '',
    requireMinHours: '',
    requireMinPoints: '',
    // Recurrence settings
    recurrenceType: 'none',
    recurrenceDayOfWeek: '',
    recurrenceDayOfMonth: '',
    recurrenceEndDate: '',
    // Time-of-day window
    scheduleStartTime: '',
    scheduleEndTime: '',
    // XP Multiplier settings
    rewardType: 'standard',
    multiplierValue: '',
    multiplierDurationHours: '',
    multiplierAppliesTo: ['all'],
    // Staff/Admin exclusion
    excludeStaff: false,
    excludeAdmins: false,
    existingMembersOnly: false,
    excludeList: false,
    // Promo code — when promoCode is non-blank, the reward is gated behind
    // the code: it stays invisible until a player redeems it (at signup or
    // from the Rewards screen). The per-player single-redemption cap is
    // enforced by the unique index on custom_reward_assignments(reward_id, user_id).
    promoCode: '',
    promoCodeSignupOnly: false,
    promoCodeMaxUses: '',
    promoCodeStartsAt: '',
    promoCodeEndsAt: '',
    sortOrder: 0,
    assignedGroupIds: [],
    assignedUserIds: [],
    // Recent Play targeting — resolved at submit-time into assignedUserIds
    activityGames: ['poker', 'cal_games'],
    activityMinHours: '',
    activityDaysWindow: '',
    activityMatchMode: 'hours',
    activityMinPercent: '',
    activityScope: 'recent',
  });

  const openSignupHighlight = async () => {
    setShowSignupHighlight(true);
    try {
      const res = await api.get('/admin/rewards/signup-highlight');
      setSignupHighlightId(res.data.rewardId || '');
    } catch (e) { /* setting is optional */ }
  };
  const saveSignupHighlight = async () => {
    setSignupHighlightSaving(true);
    try {
      await api.put('/admin/rewards/signup-highlight', { rewardId: signupHighlightId || null });
      toast.success('Signup animation reward saved');
      setShowSignupHighlight(false);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSignupHighlightSaving(false);
    }
  };

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/rewards');
      setRewards(response.data.rewards || []);
    } catch (err) {
      toast.error('Failed to load rewards');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTierConfig = async () => {
    try {
      const response = await api.get('/status-system/config');
      const tiers = response.data?.tiers || [];
      // Filter out 'member' tier and map to options format
      // API returns: key (tier key), baseName (display name)
      const options = tiers
        .filter(t => t.key !== 'member')
        .map(t => ({ value: t.key, label: t.baseName }));
      // Add Industry tier if configured
      if (response.data?.industryTier) {
        options.push({ value: response.data.industryTier.key || 'industry', label: response.data.industryTier.baseName || 'Industry' });
      }
      if (options.length > 0) {
        setTierOptions(options);
      }
    } catch (err) {
      console.error('Failed to load tier config:', err);
      // Keep default options if fetch fails
    }
  };

  const fetchUserGroups = async () => {
    try {
      const response = await api.get('/admin/user-groups');
      setUserGroups(response.data.groups || []);
    } catch (err) {
      console.error('Failed to load user groups:', err);
    }
  };

  const fetchAchievements = async () => {
    try {
      const response = await api.get('/admin/achievements');
      setAchievements(response.data.achievements || []);
    } catch (err) {
      console.error('Failed to load achievements:', err);
    }
  };

  // User search with debounce
  useEffect(() => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      setUserSearchResults([]);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const response = await api.get(`/admin/users?search=${encodeURIComponent(userSearchQuery)}&limit=20`);
        setUserSearchResults(response.data.users || []);
      } catch (err) {
        console.error('User search failed:', err);
      } finally {
        setSearchingUsers(false);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [userSearchQuery]);

  // Quick Send user search with debounce
  useEffect(() => {
    if (!qsUserSearch || qsUserSearch.length < 2) {
      setQsUserResults([]);
      return;
    }
    const timeoutId = setTimeout(async () => {
      setQsSearching(true);
      try {
        const response = await api.get(`/admin/users?search=${encodeURIComponent(qsUserSearch)}&limit=20`);
        setQsUserResults(response.data.users || []);
      } catch (err) {
        console.error('QS user search failed:', err);
      } finally {
        setQsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [qsUserSearch]);

  useEffect(() => {
    fetchRewards();
    fetchUserGroups();
    fetchAchievements();
    fetchTierConfig();
    api.get('/admin/rewards/daily-exclusive-pools')
      .then(res => setDailyPools(res.data.pools || []))
      .catch(() => {});
  }, []);

  const resetForm = () => {
    setForm({
      title: '',
      titleEs: '',
      description: '',
      descriptionEs: '',
      icon: 'gift',
      imageUrl: '',
      category: '',
      rewardValue: '',
      rewardValueType: 'dollar',
      redemptionInstructions: '',
      redemptionInstructionsEs: '',
      requiresStaffAuth: true,
      usageType: 'single',
      usageLimit: '',
      dailyExclusive: false,
      isActive: true,
      isHidden: false,
      visibleFrom: '',
      visibleUntil: '',
      expiresAt: '',
      expiresAfterDays: '',
      targetType: 'all',
      triggerType: 'none',
      triggerValue: '',
      triggerTier: '',
      triggerLevel: '',
      triggerAchievementId: '',
      // Multiple triggers
      triggerConditions: [],
      triggerLogic: 'AND',
      useMultipleTriggers: false,
      // Eligibility
      requirePlayerClubLink: false,
      requireMinTier: '',
      requireMinLevel: '',
      requireVerifiedEmail: false,
      requireMinSessions: '',
      requireMinHours: '',
      requireMinPoints: '',
      recurrenceType: 'none',
      recurrenceDayOfWeek: '',
      recurrenceDayOfMonth: '',
      recurrenceEndDate: '',
      // XP Multiplier settings
      rewardType: 'standard',
      multiplierValue: '',
      multiplierDurationHours: '',
      multiplierAppliesTo: ['all'],
      // Staff/Admin exclusion
      excludeStaff: false,
      excludeAdmins: false,
      existingMembersOnly: false,
      excludeList: false,
      // Promo code
      promoCode: '',
      promoCodeSignupOnly: false,
      promoCodeMaxUses: '',
      promoCodeStartsAt: '',
      promoCodeEndsAt: '',
      sortOrder: 0,
      assignedGroupIds: [],
      assignedUserIds: [],
    });
    setSelectedUsers([]);
    setUserSearchQuery('');
    setUserSearchResults([]);
    setActivityPreview(null);
    setActivityPreviewLoading(false);
  };

  const openCreateModal = () => {
    resetForm();
    setEditingReward(null);
    setShowModal(true);
  };

  const openDailyLimits = async () => {
    setShowDailyLimits(true);
    try {
      const res = await api.get('/admin/rewards/daily-exclusive-pools');
      setDailyLimitPools(res.data.pools || []);
    } catch (err) {
      console.error('Failed to load daily-limit pools:', err);
      toast.error('Failed to load daily limits');
    }
  };

  const addDailyLimitPool = () => {
    setDailyLimitPools(prev => [...prev, { id: `pool-${Date.now()}`, name: '', categories: [] }]);
  };

  const removeDailyLimitPool = (id) => {
    setDailyLimitPools(prev => prev.filter(p => p.id !== id));
  };

  const setDailyLimitPoolName = (id, name) => {
    setDailyLimitPools(prev => prev.map(p => (p.id === id ? { ...p, name } : p)));
  };

  // A category belongs to at most one pool: toggling it into one removes it from others.
  const toggleCategoryInPool = (poolId, cat) => {
    setDailyLimitPools(prev => prev.map(p => {
      if (p.id === poolId) {
        const has = p.categories.includes(cat);
        return { ...p, categories: has ? p.categories.filter(c => c !== cat) : [...p.categories, cat] };
      }
      return { ...p, categories: p.categories.filter(c => c !== cat) };
    }));
  };

  const saveDailyLimits = async () => {
    setDailyLimitsSaving(true);
    try {
      const pools = dailyLimitPools
        .map(p => ({ id: p.id, name: p.name, categories: p.categories }))
        .filter(p => p.categories.length > 0);
      const res = await api.put('/admin/rewards/daily-exclusive-pools', { pools });
      setDailyPools(res.data.pools || pools);
      toast.success('Daily limits saved');
      setShowDailyLimits(false);
    } catch (err) {
      console.error('Failed to save daily limits:', err);
      toast.error('Failed to save daily limits');
    } finally {
      setDailyLimitsSaving(false);
    }
  };

  const openEditModal = (reward) => {
    setEditingReward(reward);
    // Extract assigned users from assignments
    const assignedUsers = reward.assignments?.filter(a => a.userId && a.user).map(a => ({
      id: a.userId,
      email: a.user.email,
      firstName: a.user.firstName,
      lastName: a.user.lastName,
    })) || [];
    setSelectedUsers(assignedUsers);
    
    setForm({
      title: reward.title || '',
      titleEs: reward.titleEs || '',
      description: reward.description || '',
      descriptionEs: reward.descriptionEs || '',
      icon: reward.icon || 'gift',
      imageUrl: reward.imageUrl || '',
      category: reward.category || '',
      rewardValue: reward.rewardValue || '',
      rewardValueType: reward.rewardValueType || 'dollar',
      redemptionInstructions: reward.redemptionInstructions || '',
      redemptionInstructionsEs: reward.redemptionInstructionsEs || '',
      requiresStaffAuth: reward.requiresStaffAuth !== false,
      usageType: reward.usageType || 'single',
      usageLimit: reward.usageLimit || '',
      dailyExclusive: reward.dailyExclusive || false,
      isActive: reward.isActive !== false,
      isHidden: reward.isHidden === true,
      visibleFrom: utcToLocal(reward.visibleFrom),
      visibleUntil: utcToLocal(reward.visibleUntil),
      expiresAt: utcToLocal(reward.expiresAt),
      expiresAfterDays: reward.expiresAfterDays || '',
      targetType: reward.targetType || 'all',
      triggerType: reward.triggerType || 'none',
      triggerValue: reward.triggerValue || '',
      triggerTier: reward.triggerTier || '',
      triggerLevel: reward.triggerLevel || '',
      triggerAchievementId: reward.triggerAchievementId || '',
      // Multiple triggers
      triggerConditions: reward.triggerConditions || [],
      triggerLogic: reward.triggerLogic || 'AND',
      useMultipleTriggers: Array.isArray(reward.triggerConditions) && reward.triggerConditions.length > 0,
      // Eligibility
      requirePlayerClubLink: reward.requirePlayerClubLink === true,
      requireMinTier: reward.requireMinTier || '',
      requireMinLevel: reward.requireMinLevel || '',
      requireVerifiedEmail: reward.requireVerifiedEmail === true,
      requireMinSessions: reward.requireMinSessions || '',
      requireMinHours: reward.requireMinHours || '',
      requireMinPoints: reward.requireMinPoints || '',
      recurrenceType: reward.recurrenceType || 'none',
      recurrenceDayOfWeek: reward.recurrenceDayOfWeek !== null && reward.recurrenceDayOfWeek !== undefined ? reward.recurrenceDayOfWeek : '',
      recurrenceDayOfMonth: reward.recurrenceDayOfMonth || '',
      recurrenceEndDate: reward.recurrenceEndDate ? reward.recurrenceEndDate.slice(0, 16) : '',
      // Time-of-day window
      scheduleStartTime: reward.scheduleStartTime || '',
      scheduleEndTime: reward.scheduleEndTime || '',
      // XP Multiplier settings
      rewardType: reward.rewardType || 'standard',
      multiplierValue: reward.multiplierValue || '',
      multiplierDurationHours: reward.multiplierDurationHours || '',
      multiplierAppliesTo: reward.multiplierAppliesTo || ['all'],
      // Staff/Admin exclusion
      excludeStaff: reward.excludeStaff === true,
      excludeAdmins: reward.excludeAdmins === true,
      existingMembersOnly: reward.existingMembersOnly === true,
      excludeList: reward.excludeList === true,
      // Promo code
      promoCode: reward.promoCode || '',
      promoCodeSignupOnly: reward.promoCodeSignupOnly === true,
      promoCodeMaxUses: reward.promoCodeMaxUses || '',
      promoCodeStartsAt: utcToLocal(reward.promoCodeStartsAt),
      promoCodeEndsAt: utcToLocal(reward.promoCodeEndsAt),
      sortOrder: reward.sortOrder || 0,
      assignedGroupIds: reward.assignments?.filter(a => a.groupId).map(a => a.groupId) || [],
      assignedUserIds: assignedUsers.map(u => u.id),
      // Recent Play fields are create/edit-time only — not persisted on the reward model
      activityGames: ['poker', 'cal_games'],
      activityMinHours: '',
      activityDaysWindow: '',
      activityMatchMode: 'hours',
      activityMinPercent: '',
      activityScope: 'recent',
    });
    setActivityPreview(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    // Validate XP multiplier fields
    if (form.rewardType === 'xp_multiplier') {
      if (!form.multiplierValue || parseFloat(form.multiplierValue) <= 1) {
        toast.error('XP multiplier value must be greater than 1');
        return;
      }
      // Duration no longer required - uses visibility schedule instead
    }

    // Validate Recent Play targeting at submit-time
    let activityFilterPayload = null;
    if (form.targetType === 'activity') {
      const games = (form.activityGames || []).filter(g => g === 'poker' || g === 'cal_games');
      const minHours = parseFloat(form.activityMinHours);
      const matchMode = form.activityMatchMode === 'percent' ? 'percent' : 'hours';
      const scope = form.activityScope === 'lifetime' ? 'lifetime' : 'recent';
      const daysWindow = parseInt(form.activityDaysWindow, 10);
      const minPercent = parseFloat(form.activityMinPercent);
      if (games.length === 0) {
        toast.error('Select at least one game type (Poker or Cal Games)');
        return;
      }
      if (!(minHours > 0)) {
        toast.error(matchMode === 'percent' ? 'Minimum total hours must be greater than 0' : 'Minimum hours must be greater than 0');
        return;
      }
      if (scope === 'recent' && !(daysWindow > 0)) {
        toast.error('Days window must be greater than 0');
        return;
      }
      if (matchMode === 'percent' && !(minPercent > 0 && minPercent <= 100)) {
        toast.error('Minimum % must be between 1 and 100');
        return;
      }
      activityFilterPayload = { games, minHours, daysWindow, matchMode, scope };
      if (matchMode === 'percent') activityFilterPayload.minPercent = minPercent;
    }

    try {
      const payload = {
        ...form,
        rewardValue: form.rewardValue ? parseFloat(form.rewardValue) : null,
        usageLimit: form.usageLimit ? parseInt(form.usageLimit) : null,
        triggerValue: form.triggerValue ? parseInt(form.triggerValue) : null,
        triggerTier: form.triggerTier || null,
        triggerLevel: form.triggerLevel ? parseInt(form.triggerLevel) : null,
        triggerAchievementId: form.triggerAchievementId || null,
        // Multiple triggers - only send if using multiple triggers mode
        triggerConditions: form.useMultipleTriggers ? form.triggerConditions : null,
        triggerLogic: form.useMultipleTriggers ? form.triggerLogic : 'AND',
        visibleFrom: localToUtc(form.visibleFrom),
        visibleUntil: localToUtc(form.visibleUntil),
        expiresAt: localToUtc(form.expiresAt),
        expiresAfterDays: form.expiresAfterDays ? parseInt(form.expiresAfterDays) : null,
        requirePlayerClubLink: form.requirePlayerClubLink || false,
        requireMinTier: form.requireMinTier || null,
        requireMinLevel: form.requireMinLevel ? parseInt(form.requireMinLevel) : null,
        requireVerifiedEmail: form.requireVerifiedEmail || false,
        requireMinSessions: form.requireMinSessions ? parseInt(form.requireMinSessions) : null,
        requireMinHours: form.requireMinHours ? parseFloat(form.requireMinHours) : null,
        requireMinPoints: form.requireMinPoints ? parseInt(form.requireMinPoints) : null,
        recurrenceType: form.recurrenceType || 'none',
        recurrenceDayOfWeek: form.recurrenceDayOfWeek !== '' ? parseInt(form.recurrenceDayOfWeek) : null,
        recurrenceDayOfMonth: form.recurrenceDayOfMonth ? parseInt(form.recurrenceDayOfMonth) : null,
        recurrenceEndDate: form.recurrenceEndDate || null,
        // Time-of-day window
        scheduleStartTime: form.scheduleStartTime || null,
        scheduleEndTime: form.scheduleEndTime || null,
        // XP Multiplier fields
        rewardType: form.rewardType || 'standard',
        multiplierValue: form.multiplierValue ? parseFloat(form.multiplierValue) : null,
        multiplierDurationHours: form.multiplierDurationHours ? parseInt(form.multiplierDurationHours) : null,
        multiplierAppliesTo: form.rewardType === 'xp_multiplier' ? form.multiplierAppliesTo : null,
        // Promo code — blank means "no code required", anything else becomes
        // a gated reward. Backend uppercases on save, but we mirror it here so
        // the admin form preview matches what'll be stored.
        promoCode: form.promoCode && form.promoCode.trim() ? form.promoCode.trim().toUpperCase() : null,
        promoCodeSignupOnly: !!form.promoCodeSignupOnly,
        promoCodeMaxUses: form.promoCodeMaxUses ? parseInt(form.promoCodeMaxUses, 10) : null,
        promoCodeStartsAt: localToUtc(form.promoCodeStartsAt),
        promoCodeEndsAt: localToUtc(form.promoCodeEndsAt),
        assignedUserIds: selectedUsers.map(u => u.id),
      };

      // Remove the useMultipleTriggers flag from payload (it's just for UI state)
      delete payload.useMultipleTriggers;

      // Strip Recent Play UI fields — backend only wants the nested activityFilter
      delete payload.activityGames;
      delete payload.activityMinHours;
      delete payload.activityDaysWindow;
      delete payload.activityMatchMode;
      delete payload.activityMinPercent;
      delete payload.activityScope;

      if (activityFilterPayload) {
        payload.activityFilter = activityFilterPayload;
        // targetType='activity' is UI-only; backend resolves to users and stores as 'users'
        payload.targetType = 'users';
      }

      if (editingReward) {
        const res = await api.put(`/admin/rewards/${editingReward.id}`, payload);
        if (res.data?.activityFilter) {
          toast.success(`Reward updated — ${res.data.activityFilter.matched} user${res.data.activityFilter.matched === 1 ? '' : 's'} added from Recent Play`);
        } else {
          toast.success('Reward updated');
        }
      } else {
        const res = await api.post('/admin/rewards', payload);
        if (res.data?.activityFilter) {
          toast.success(`Reward created — ${res.data.activityFilter.matched} user${res.data.activityFilter.matched === 1 ? '' : 's'} assigned from Recent Play`);
        } else {
          toast.success('Reward created');
        }
      }

      setShowModal(false);
      fetchRewards();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save reward');
    }
  };

  const handleDelete = async (reward) => {
    if (!confirm(`Delete "${reward.title}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/admin/rewards/${reward.id}`);
      toast.success('Reward deleted');
      fetchRewards();
    } catch (err) {
      toast.error('Failed to delete reward');
    }
  };

  const handleToggleActive = async (reward) => {
    try {
      await api.post(`/admin/rewards/${reward.id}/toggle-active`);
      toast.success(reward.isActive ? 'Reward deactivated' : 'Reward activated');
      fetchRewards();
    } catch (err) {
      toast.error('Failed to update reward');
    }
  };

  const handleToggleHidden = async (reward) => {
    try {
      await api.post(`/admin/rewards/${reward.id}/toggle-hidden`);
      toast.success(reward.isHidden ? 'Reward now visible' : 'Reward now hidden');
      fetchRewards();
    } catch (err) {
      toast.error('Failed to update reward');
    }
  };

  // ── Quick Send ──
  const resetQuickSend = () => {
    setQuickSendForm({
      title: '', description: '', rewardValue: '', rewardValueType: 'dollar', category: '',
      icon: 'gift', requiresStaffAuth: true, expiresAfterDays: '', expiresUnit: 'days', expiresAtExact: '',
      sendNotification: true, notificationMessage: '', recipientMode: 'users', tierFilter: '',
      scheduledFor: null,
      excludeList: false,
      activityGames: ['poker', 'cal_games'],
      activityMinHours: '',
      activityDaysWindow: '',
      activityMatchMode: 'hours',
      activityMinPercent: '',
      activityScope: 'recent',
    });
    setQsSelectedUsers([]);
    setQsSelectedGroups([]);
    setQsPastedNumbers('');
    setQsUserSearch('');
    setQsUserResults([]);
    setQsActivityPreview(null);
    setQsActivityPreviewLoading(false);
    setQsUnmatched([]);
    setQsExcluded([]);
  };

  const fetchPendingAssignments = async () => {
    setLoadingPending(true);
    try {
      const response = await api.get('/admin/rewards/pending-assignments');
      setPendingData(response.data);
    } catch (err) {
      console.error('Failed to fetch pending assignments:', err);
      toast.error('Failed to load pending assignments');
    } finally {
      setLoadingPending(false);
    }
  };

  // Delete a single pending reward assignment (used to clean up duplicate sends).
  // Used from the Pending panel — confirmation prompt shows what's being removed.
  const deletePendingAssignment = async (assignment) => {
    const recipientLabel = assignment.memberNumber
      ? `member #${assignment.memberNumber}`
      : (assignment.user
          ? `${[assignment.user.firstName, assignment.user.lastName].filter(Boolean).join(' ') || assignment.user.email}`
          : 'this recipient');
    const rewardLabel = assignment.reward?.title || 'this reward';
    if (!window.confirm(`Delete pending "${rewardLabel}" for ${recipientLabel}?\n\nThis cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/rewards/assignments/${assignment.id}`);
      toast.success('Pending reward deleted');
      // Optimistic local update so the row disappears immediately, then refresh
      setPendingData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          pending: (prev.pending || []).filter((a) => a.id !== assignment.id),
          summary: {
            ...(prev.summary || {}),
            totalPending: Math.max(0, (prev.summary?.totalPending || 0) - 1),
          },
        };
      });
      fetchPendingAssignments();
    } catch (err) {
      console.error('Failed to delete pending assignment:', err);
      toast.error(err.response?.data?.error || 'Failed to delete pending reward');
    }
  };

  const openQuickSend = () => {
    resetQuickSend();
    setShowQuickSend(true);
  };

  const openBulkDeactivate = () => {
    setBulkDays('30');
    setBulkQuickSendOnly(true);
    setBulkPreview(null);
    setShowBulkDeactivate(true);
  };

  const bulkPayload = () => ({
    olderThanDays: parseInt(bulkDays, 10),
    quickSendOnly: bulkQuickSendOnly,
  });

  const previewBulkDeactivate = async () => {
    const days = parseInt(bulkDays, 10);
    if (!(days >= 0)) { toast.error('Enter a number of days (0 or more)'); return; }
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/rewards/bulk-deactivate', { ...bulkPayload(), dryRun: true });
      setBulkPreview({ matched: res.data?.matched ?? 0 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to preview');
    } finally {
      setBulkBusy(false);
    }
  };

  const runBulkDeactivate = async () => {
    const days = parseInt(bulkDays, 10);
    if (!(days >= 0)) { toast.error('Enter a number of days (0 or more)'); return; }
    setBulkBusy(true);
    try {
      const res = await api.post('/admin/rewards/bulk-deactivate', { ...bulkPayload(), dryRun: false });
      const n = res.data?.deactivated ?? 0;
      toast.success(`Deactivated ${n} reward${n === 1 ? '' : 's'}`);
      setShowBulkDeactivate(false);
      fetchRewards();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to deactivate');
    } finally {
      setBulkBusy(false);
    }
  };

  // Preview how many users match the activity filter on the regular create/edit form.
  // Reuses the same /quick-send/preview-activity endpoint since the resolution is identical.
  const previewFormActivityFilter = async () => {
    const games = (form.activityGames || []).filter(g => g === 'poker' || g === 'cal_games');
    const minHours = parseFloat(form.activityMinHours);
    const matchMode = form.activityMatchMode === 'percent' ? 'percent' : 'hours';
    const scope = form.activityScope === 'lifetime' ? 'lifetime' : 'recent';
    const daysWindow = parseInt(form.activityDaysWindow, 10);
    const minPercent = parseFloat(form.activityMinPercent);
    if (games.length === 0 || !(minHours > 0) ||
        (scope === 'recent' && !(daysWindow > 0)) ||
        (matchMode === 'percent' && !(minPercent > 0 && minPercent <= 100))) {
      toast.error('Fill in the targeting fields first');
      return;
    }
    setActivityPreviewLoading(true);
    try {
      const response = await api.post('/admin/rewards/quick-send/preview-activity', {
        games, minHours, daysWindow, matchMode, scope, minPercent,
      });
      setActivityPreview({
        matched: response.data.matched,
        candidates: response.data.candidates,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to preview filter');
    } finally {
      setActivityPreviewLoading(false);
    }
  };

  // Preview how many users match the current activity filter
  const previewActivityFilter = async () => {
    const games = (quickSendForm.activityGames || []).filter(g => g === 'poker' || g === 'cal_games');
    const minHours = parseFloat(quickSendForm.activityMinHours);
    const matchMode = quickSendForm.activityMatchMode === 'percent' ? 'percent' : 'hours';
    const scope = quickSendForm.activityScope === 'lifetime' ? 'lifetime' : 'recent';
    const daysWindow = parseInt(quickSendForm.activityDaysWindow, 10);
    const minPercent = parseFloat(quickSendForm.activityMinPercent);
    if (games.length === 0 || !(minHours > 0) ||
        (scope === 'recent' && !(daysWindow > 0)) ||
        (matchMode === 'percent' && !(minPercent > 0 && minPercent <= 100))) {
      toast.error('Fill in the targeting fields first');
      return;
    }
    setQsActivityPreviewLoading(true);
    try {
      const response = await api.post('/admin/rewards/quick-send/preview-activity', {
        games, minHours, daysWindow, matchMode, scope, minPercent,
      });
      setQsActivityPreview({
        matched: response.data.matched,
        candidates: response.data.candidates,
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to preview filter');
    } finally {
      setQsActivityPreviewLoading(false);
    }
  };

  // ── Notify Recipients handlers ──
  const openNotifyRecipients = (reward) => {
    setNotifyPrimaryRewardId(reward.id);
    setNotifySelectedRewardIds([reward.id]);
    setNotifyForm({
      title: '🎁 You have a new reward!',
      body: `A "${reward.title}" reward is now available in your Player's Club app. Open the app to redeem.`,
    });
    setNotifyPreview(null);
    setShowNotifyRecipients(true);
  };

  const closeNotifyRecipients = () => {
    setShowNotifyRecipients(false);
    setNotifyPrimaryRewardId(null);
    setNotifySelectedRewardIds([]);
    setNotifyForm({ title: '', body: '' });
    setNotifyPreview(null);
  };

  const toggleNotifyRewardId = (rewardId) => {
    setNotifySelectedRewardIds((prev) =>
      prev.includes(rewardId) ? prev.filter((id) => id !== rewardId) : [...prev, rewardId]
    );
  };

  // Debounced preview when selection changes
  useEffect(() => {
    if (!showNotifyRecipients || notifySelectedRewardIds.length === 0) {
      setNotifyPreview(null);
      return;
    }
    let cancelled = false;
    setNotifyPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const response = await api.post('/admin/rewards/notify-recipients/preview', {
          rewardIds: notifySelectedRewardIds,
        });
        if (!cancelled) {
          setNotifyPreview({
            userCount: response.data.userCount,
            candidatePoolSize: response.data.candidatePoolSize || 0,
            filteredOut: response.data.filteredOut || 0,
            perReward: response.data.perReward || [],
          });
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.error || 'Failed to preview recipients');
          setNotifyPreview(null);
        }
      } finally {
        if (!cancelled) setNotifyPreviewLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [showNotifyRecipients, notifySelectedRewardIds]);

  const sendNotifyRecipients = async () => {
    if (!notifyForm.title.trim() || !notifyForm.body.trim()) {
      toast.error('Title and message body are required');
      return;
    }
    if (notifySelectedRewardIds.length === 0) {
      toast.error('Select at least one reward');
      return;
    }
    if (!notifyPreview || notifyPreview.userCount === 0) {
      toast.error('No recipients to notify');
      return;
    }
    const confirmed = window.confirm(
      `Send notification to ${notifyPreview.userCount} unique recipient${notifyPreview.userCount === 1 ? '' : 's'}?`
    );
    if (!confirmed) return;

    setNotifySending(true);
    try {
      const response = await api.post('/admin/rewards/notify-recipients', {
        rewardIds: notifySelectedRewardIds,
        title: notifyForm.title.trim(),
        body: notifyForm.body.trim(),
      });
      const stats = response.data.stats || {};
      toast.success(
        `Sent to ${stats.successCount || 0} of ${stats.uniqueRecipients || 0} recipients` +
        (stats.failCount ? ` (${stats.failCount} failed)` : '')
      );
      closeNotifyRecipients();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send notifications');
    } finally {
      setNotifySending(false);
    }
  };

  const toggleQsUser = (user) => {
    const exists = qsSelectedUsers.find(u => u.id === user.id);
    if (exists) {
      setQsSelectedUsers(qsSelectedUsers.filter(u => u.id !== user.id));
    } else {
      setQsSelectedUsers([...qsSelectedUsers, user]);
    }
    setQsUserSearch('');
    setQsUserResults([]);
  };

  const handleQuickSend = async () => {
    if (!quickSendForm.title.trim()) {
      toast.error('Title is required');
      return;
    }

    // Validate schedule date if set
    if (quickSendForm.scheduledFor !== null && !quickSendForm.scheduledFor) {
      toast.error('Please select a date and time for scheduling');
      return;
    }
    if (quickSendForm.scheduledFor && new Date(quickSendForm.scheduledFor) <= new Date()) {
      toast.error('Scheduled time must be in the future');
      return;
    }
    if (quickSendForm.expiresUnit === 'exact' && quickSendForm.expiresAtExact && new Date(quickSendForm.expiresAtExact) <= new Date()) {
      toast.error('Exact expiration must be in the future');
      return;
    }

    // Build recipients
    const recipients = {};
    const mode = quickSendForm.recipientMode;

    if (mode === 'users') {
      if (qsSelectedUsers.length === 0) {
        toast.error('Select at least one player');
        return;
      }
      recipients.userIds = qsSelectedUsers.map(u => u.id);
    } else if (mode === 'tier') {
      if (!quickSendForm.tierFilter) {
        toast.error('Select a tier');
        return;
      }
      recipients.tierFilter = quickSendForm.tierFilter;
    } else if (mode === 'groups') {
      if (qsSelectedGroups.length === 0) {
        toast.error('Select at least one group');
        return;
      }
      recipients.groupIds = qsSelectedGroups;
    } else if (mode === 'paste') {
      const numbers = qsPastedNumbers
        .split(/[\n,;\s]+/)
        .map(n => n.trim())
        .filter(n => n.length > 0);
      if (numbers.length === 0) {
        toast.error('Enter at least one member number');
        return;
      }
      recipients.memberNumbers = numbers;
    } else if (mode === 'activity') {
      const games = (quickSendForm.activityGames || []).filter(g => g === 'poker' || g === 'cal_games');
      const minHours = parseFloat(quickSendForm.activityMinHours);
      const matchMode = quickSendForm.activityMatchMode === 'percent' ? 'percent' : 'hours';
      const scope = quickSendForm.activityScope === 'lifetime' ? 'lifetime' : 'recent';
      const daysWindow = parseInt(quickSendForm.activityDaysWindow, 10);
      const minPercent = parseFloat(quickSendForm.activityMinPercent);
      if (games.length === 0) {
        toast.error('Select at least one game (Poker or Cal Games)');
        return;
      }
      if (!(minHours > 0)) {
        toast.error(matchMode === 'percent' ? 'Enter a minimum total hours value greater than 0' : 'Enter a minimum hours value greater than 0');
        return;
      }
      if (scope === 'recent' && !(daysWindow > 0)) {
        toast.error('Enter a days window greater than 0');
        return;
      }
      if (matchMode === 'percent' && !(minPercent > 0 && minPercent <= 100)) {
        toast.error('Enter a minimum % between 1 and 100');
        return;
      }
      recipients.activityFilter = { games, minHours, daysWindow, matchMode, scope };
      if (matchMode === 'percent') recipients.activityFilter.minPercent = minPercent;
    }

    setQsSending(true);
    setQsUnmatched([]);
    try {
      const payload = {
        title: quickSendForm.title.trim(),
        description: quickSendForm.description.trim() || undefined,
        rewardValue: quickSendForm.rewardValue ? parseFloat(quickSendForm.rewardValue) : undefined,
        rewardValueType: quickSendForm.rewardValueType,
        category: quickSendForm.category || undefined,
        icon: quickSendForm.icon,
        requiresStaffAuth: quickSendForm.requiresStaffAuth,
        expiresAfterDays: (quickSendForm.expiresUnit === 'days' && quickSendForm.expiresAfterDays) ? parseInt(quickSendForm.expiresAfterDays) : undefined,
        expiresAfterHours: (quickSendForm.expiresUnit === 'hours' && quickSendForm.expiresAfterDays) ? parseInt(quickSendForm.expiresAfterDays) : undefined,
        expiresAt: (quickSendForm.expiresUnit === 'exact' && quickSendForm.expiresAtExact) ? localToUtc(quickSendForm.expiresAtExact) : undefined,
        sendNotification: quickSendForm.sendNotification,
        notificationMessage: quickSendForm.notificationMessage.trim() || undefined,
        scheduledFor: quickSendForm.scheduledFor ? new Date(quickSendForm.scheduledFor).toISOString() : undefined,
        excludeList: quickSendForm.excludeList === true,
        recipients,
      };

      const response = await api.post('/admin/rewards/quick-send', payload);
      const s = response.data.summary || {};
      const isScheduled = !!response.data.scheduled;
      const unmatched = Array.isArray(s.pendingMemberNumbers) ? s.pendingMemberNumbers : [];
      const excluded = Array.isArray(s.excluded) ? s.excluded : [];
      setQsExcluded(excluded);
      const parts = isScheduled
        ? [`Reward scheduled for ${new Date(response.data.scheduled).toLocaleString()} — ${s.assigned} player${s.assigned !== 1 ? 's' : ''}`]
        : [`${s.assigned} player${s.assigned !== 1 ? 's' : ''} received the reward`];
      if (excluded.length > 0) {
        parts.push(`${excluded.length} on the exclusion list skipped`);
      }
      // Suppress the "0 received" success toast when everyone was pending —
      // the acknowledgment step below explains the outcome instead.
      if (!(unmatched.length > 0 && (s.assigned || 0) === 0)) {
        toast.success(parts.join('. '));
      }
      fetchRewards();

      if (unmatched.length > 0) {
        // Pending (no-account) recipients: require an explicit acknowledgment so
        // staff clearly understand the reward was NOT delivered and the member
        // must download the app to receive it (it applies when they link).
        setQsAck({ unmatched, assigned: s.assigned || 0, title: quickSendForm.title.trim() });
      } else if (excluded.length > 0) {
        // Keep the modal open so staff can see who was skipped by the exclusion list.
        setQsUnmatched([]);
      } else {
        setQsUnmatched([]);
        setShowQuickSend(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reward');
    } finally {
      setQsSending(false);
    }
  };

  // Helper to get tier display name from tierOptions
  const getTierLabel = (tierId) => {
    const tier = tierOptions.find(t => t.value === tierId);
    return tier?.label || (tierId ? tierId.charAt(0).toUpperCase() + tierId.slice(1) : '');
  };

  // User selection helpers
  const toggleUserSelection = (user) => {
    const exists = selectedUsers.find(u => u.id === user.id);
    if (exists) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
    setUserSearchQuery('');
    setUserSearchResults([]);
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  // Get trigger type config
  const getTriggerConfig = () => {
    return TRIGGER_TYPES.find(t => t.value === form.triggerType) || {};
  };

  const filteredRewards = rewards.filter(r => {
    // Status filter
    if (filter === 'active' && !r.isActive) return false;
    if (filter === 'inactive' && r.isActive) return false;
    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchesTitle = (r.title || '').toLowerCase().includes(q);
      const matchesDesc = (r.description || '').toLowerCase().includes(q);
      const matchesCategory = (r.category || '').toLowerCase().includes(q);
      const matchesValue = r.rewardValue && String(r.rewardValue).includes(q);
      const matchesTrigger = (r.triggerType || '').toLowerCase().includes(q);
      if (!matchesTitle && !matchesDesc && !matchesCategory && !matchesValue && !matchesTrigger) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Gift className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Custom Rewards</h1>
          <span className="text-gray-500">
            {searchQuery ? `(${filteredRewards.length} of ${rewards.length})` : `(${rewards.length})`}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search rewards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-gray-700 border border-gray-600 rounded-lg pl-9 pr-8 py-2 text-white text-sm placeholder-gray-400 w-52 focus:outline-none focus:border-amber-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="all">All Rewards</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
          <button
            onClick={() => { setShowPending(!showPending); if (!pendingData) fetchPendingAssignments(); }}
            className={`flex items-center gap-2 px-4 py-2 ${showPending ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-600/30 hover:bg-purple-600/50'} text-white font-medium rounded-lg`}
          >
            <Clock size={18} /> Pending
            {pendingData?.summary?.totalPending > 0 && (
              <span className="bg-purple-400 text-black text-xs font-bold px-1.5 py-0.5 rounded-full">{pendingData.summary.totalPending}</span>
            )}
          </button>
          <button
            onClick={openQuickSend}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-500"
          >
            <Send size={18} /> Quick Send
          </button>
          <button
            onClick={openDailyLimits}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-500"
          >
            <Clock size={18} /> Daily Limits
          </button>
          <button
            onClick={() => setShowExclusionList(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white font-medium rounded-lg hover:bg-red-600"
          >
            <Shield size={18} /> Exclusion List
          </button>
          <button
            onClick={openSignupHighlight}
            className="flex items-center gap-2 px-4 py-2 bg-fuchsia-700 text-white font-medium rounded-lg hover:bg-fuchsia-600"
          >
            <Sparkles size={18} /> Signup Animation
          </button>
          <button
            onClick={openBulkDeactivate}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-200 font-medium rounded-lg hover:bg-gray-600 border border-gray-600"
          >
            <Archive size={18} /> Bulk Deactivate
          </button>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
          >
            <Plus size={18} /> New Reward
          </button>
          <button 
            onClick={fetchRewards} 
            className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Reward Exclusion List manager */}
      <ExclusionListModal open={showExclusionList} onClose={() => setShowExclusionList(false)} />

      {/* Signup animation reward picker */}
      {showSignupHighlight && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowSignupHighlight(false)}>
          <div className="bg-gray-800 rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <div className="flex items-center gap-2"><Sparkles size={20} className="text-fuchsia-400" /><h3 className="text-lg font-semibold text-white">Signup Animation Reward</h3></div>
              <button onClick={() => setShowSignupHighlight(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-400">Pick which reward the onboarding celebration shows after a player links their Player&apos;s Club account. Only rewards with the &quot;On Signup&quot; trigger appear here.</p>
              <select value={signupHighlightId} onChange={(e) => setSignupHighlightId(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white">
                <option value="">Automatic (first active signup reward)</option>
                {rewards.filter((r) => r.triggerType === 'signup').map((r) => (
                  <option key={r.id} value={r.id}>{r.title}{r.rewardValue ? ` — $${r.rewardValue}` : ''}{r.isActive === false ? ' (inactive)' : ''}</option>
                ))}
              </select>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowSignupHighlight(false)} className="px-4 py-2 text-gray-300 hover:text-white">Cancel</button>
                <button onClick={saveSignupHighlight} disabled={signupHighlightSaving} className="px-4 py-2 bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-500 disabled:opacity-50">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Limits by Category Modal */}
      {showDailyLimits && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowDailyLimits(false)}>
          <div className="bg-gray-800 rounded-xl border border-gray-700 shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Clock className="text-indigo-400" size={20} />
                <h3 className="text-lg font-semibold text-white">Daily Limits</h3>
              </div>
              <button onClick={() => setShowDailyLimits(false)} className="text-gray-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-gray-400 mb-4">
                Each <span className="text-white font-medium">pool</span> limits players to one redemption per day
                across all of its categories (resets 6 AM Pacific). A category on its own is just a one-category pool.
                Pools are independent of each other and of the per-reward &quot;one per day&quot; toggle.
                &quot;Uncategorized&quot; covers rewards with no category, incl. Quick Send.
              </p>
              <div className="space-y-3">
                {dailyLimitPools.map((pool, idx) => (
                  <div key={pool.id} className="bg-gray-700/30 border border-gray-700 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={pool.name}
                        onChange={(e) => setDailyLimitPoolName(pool.id, e.target.value)}
                        placeholder={`Pool ${idx + 1} name (optional)`}
                        className="flex-1 px-3 py-1.5 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-indigo-500"
                      />
                      <button onClick={() => removeDailyLimitPool(pool.id)} className="p-1.5 text-gray-400 hover:text-red-400" title="Remove pool">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[...CATEGORIES, 'Uncategorized'].map(cat => {
                        const inThis = pool.categories.includes(cat);
                        const otherPool = !inThis && dailyLimitPools.find(p => p.id !== pool.id && p.categories.includes(cat));
                        return (
                          <button
                            key={cat}
                            disabled={!!otherPool}
                            onClick={() => toggleCategoryInPool(pool.id, cat)}
                            title={otherPool ? `Already in pool: ${otherPool.name || 'unnamed'}` : ''}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              inThis
                                ? 'bg-indigo-600 border-indigo-500 text-white'
                                : otherPool
                                ? 'bg-gray-800 border-gray-700 text-gray-600 cursor-not-allowed'
                                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-indigo-500'
                            }`}
                          >
                            {cat}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {dailyLimitPools.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-4">No daily limits set. Add a pool to start.</p>
                )}
              </div>
              <button
                onClick={addDailyLimitPool}
                className="mt-3 flex items-center gap-2 px-3 py-2 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 text-sm font-medium rounded-lg"
              >
                <Plus size={16} /> Add pool
              </button>
            </div>
            <div className="flex gap-2 p-5 border-t border-gray-700">
              <button
                onClick={() => setShowDailyLimits(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveDailyLimits}
                disabled={dailyLimitsSaving}
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium rounded-lg"
              >
                {dailyLimitsSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Automated Win-Backs */}
      <WinbackAutomationPanel />

      {/* Pending Assignments Panel */}
      {showPending && (
        <div className="bg-gray-800 rounded-xl border border-purple-500/30 overflow-hidden">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="text-purple-400" size={20} />
              <h2 className="text-lg font-semibold text-white">Pending Reward Assignments</h2>
              <span className="text-sm text-gray-400">
                Rewards waiting for players to create an account &amp; link their Player's Club card
              </span>
            </div>
            <button
              onClick={fetchPendingAssignments}
              className="p-1.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
            >
              <RefreshCw size={16} className={loadingPending ? 'animate-spin' : ''} />
            </button>
          </div>

          {loadingPending && !pendingData ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
            </div>
          ) : pendingData?.pending?.length === 0 && pendingData?.claimed?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock className="mx-auto mb-2" size={32} />
              <p>No pending assignments. Use Quick Send with member numbers to pre-assign rewards.</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Pending (unclaimed) */}
              {pendingData?.pending?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <AlertCircle size={14} /> Waiting ({pendingData.pending.length})
                  </h3>
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-700">
                          <th className="px-4 py-2">Member #</th>
                          <th className="px-4 py-2">Reward</th>
                          <th className="px-4 py-2">Value</th>
                          <th className="px-4 py-2">Assigned</th>
                          <th className="px-4 py-2">Expires</th>
                          <th className="px-4 py-2">Status</th>
                          <th className="px-4 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingData.pending.map(a => (
                          <tr key={a.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                            <td className="px-4 py-2 text-white font-mono">{a.memberNumber}</td>
                            <td className="px-4 py-2 text-white">{a.reward?.title || 'Unknown'}</td>
                            <td className="px-4 py-2 text-amber-400">
                              {a.reward?.rewardValue ? (a.reward.rewardValueType === 'percent' ? `${a.reward.rewardValue}%` : `$${a.reward.rewardValue}`) : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-400">{new Date(a.assignedAt).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              {a.expiresAt ? (
                                <span className={a.isExpired ? 'text-red-400' : 'text-gray-400'}>
                                  {new Date(a.expiresAt).toLocaleDateString()}
                                </span>
                              ) : <span className="text-gray-600">Never</span>}
                            </td>
                            <td className="px-4 py-2">
                              {a.isExpired ? (
                                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Expired</span>
                              ) : (
                                <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Waiting</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => deletePendingAssignment(a)}
                                title="Delete this pending reward (e.g. duplicate send)"
                                className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Claimed (completed) */}
              {pendingData?.claimed?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                    <CheckCircle size={14} /> Claimed ({pendingData.claimed.length})
                  </h3>
                  <div className="bg-gray-900 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-400 border-b border-gray-700">
                          <th className="px-4 py-2">Member #</th>
                          <th className="px-4 py-2">Reward</th>
                          <th className="px-4 py-2">Value</th>
                          <th className="px-4 py-2">Claimed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingData.claimed.map(a => (
                          <tr key={a.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                            <td className="px-4 py-2 text-white font-mono">{a.memberNumber}</td>
                            <td className="px-4 py-2 text-white">{a.reward?.title || 'Unknown'}</td>
                            <td className="px-4 py-2 text-amber-400">
                              {a.reward?.rewardValue ? (a.reward.rewardValueType === 'percent' ? `${a.reward.rewardValue}%` : `$${a.reward.rewardValue}`) : '—'}
                            </td>
                            <td className="px-4 py-2 text-emerald-400">{a.claimedBy || 'Unknown'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Rewards List */}
      <div className="space-y-3">
        {filteredRewards.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-8 text-center">
            <Gift className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400">No rewards found</p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-amber-500 hover:text-amber-400"
            >
              Create your first reward
            </button>
          </div>
        ) : (
          filteredRewards.map((reward) => (
            <div
              key={reward.id}
              className={`bg-gray-800 rounded-xl overflow-hidden transition-all ${
                !reward.isActive ? 'opacity-60' : ''
              }`}
            >
              {/* Reward Header */}
              <div className="p-4 flex items-center gap-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  reward.isActive ? 'bg-amber-500/20' : 'bg-gray-700'
                }`}>
                  <Gift className={reward.isActive ? 'text-amber-500' : 'text-gray-500'} size={24} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-white font-semibold truncate">{reward.title}</h3>
                    {reward.rewardValue && (
                      <span className="text-amber-500 text-sm">
                        {reward.rewardValueType === 'percent' ? `${reward.rewardValue}%` : `$${reward.rewardValue}`}
                      </span>
                    )}
                    {reward.category && (
                      <span className="text-xs bg-gray-700 text-gray-300 px-2 py-0.5 rounded">
                        {reward.category}
                      </span>
                    )}
                    {(() => {
                      const cat = (reward.category && reward.category.trim()) ? reward.category : 'Uncategorized';
                      const pool = dailyPools.find(p => p.categories?.includes(cat));
                      if (reward.dailyExclusive) {
                        return (
                          <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded flex items-center gap-1" title="One per day across all toggled rewards">
                            <Clock size={12} /> 1/day
                          </span>
                        );
                      }
                      if (pool) {
                        const name = pool.name || (pool.categories || []).join(' + ');
                        return (
                          <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded flex items-center gap-1" title={`Daily limit pool: ${name}`}>
                            <Clock size={12} /> 1/day · {name}
                          </span>
                        );
                      }
                      return null;
                    })()}
                    {reward.requiresStaffAuth && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <UserCheck size={12} /> Staff Auth
                      </span>
                    )}
                    {reward.excludeStaff && (
                      <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Shield size={12} /> Staff Excluded
                      </span>
                    )}
                    {reward.excludeAdmins && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Shield size={12} /> Admins Excluded
                      </span>
                    )}
                    {reward.triggerType === 'referral_made' && (
                      <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <UserPlus size={12} /> Referrer Reward
                      </span>
                    )}
                    {reward.triggerType === 'referral_session' && (
                      <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <UserPlus size={12} /> Referral Played
                      </span>
                    )}
                    {reward.triggerType === 'referral_received' && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <UserPlus size={12} /> New User Reward
                      </span>
                    )}
                    {/* Tier-based trigger badges */}
                    {reward.triggerType === 'tier_achieved' && reward.triggerTier && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Crown size={12} /> {getTierLabel(reward.triggerTier)} ↑
                      </span>
                    )}
                    {reward.triggerType === 'tier_current' && reward.triggerTier && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Crown size={12} /> {getTierLabel(reward.triggerTier)}+
                      </span>
                    )}
                    {reward.triggerType === 'level_achieved' && reward.triggerLevel && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <TrendingUp size={12} /> Level {reward.triggerLevel} ↑
                      </span>
                    )}
                    {reward.triggerType === 'level_current' && reward.triggerLevel && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <TrendingUp size={12} /> Level {reward.triggerLevel}+
                      </span>
                    )}
                    {/* Other trigger badges */}
                    {reward.triggerType === 'birthday' && (
                      <span className="text-xs bg-pink-500/20 text-pink-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Cake size={12} /> Birthday
                      </span>
                    )}
                    {reward.triggerType === 'signup' && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Zap size={12} /> Welcome
                      </span>
                    )}
                    {reward.triggerType === 'first_session' && (
                      <span className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Star size={12} /> First Session
                      </span>
                    )}
                    {reward.triggerType === 'achievement_unlocked' && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Trophy size={12} /> Achievement
                      </span>
                    )}
                    {reward.triggerType === 'review_confirmed' && (
                      <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Star size={12} /> Google Review
                      </span>
                    )}
                    {/* Value type badge for percentage rewards */}
                    {reward.rewardValueType === 'percent' && reward.rewardValue && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Percent size={12} /> {reward.rewardValue}% Off
                      </span>
                    )}
                    {reward.requirePlayerClubLink && (
                      <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <Link size={12} /> PC Required
                      </span>
                    )}
                    {reward.isHidden && (
                      <span className="text-xs bg-gray-600 text-gray-300 px-2 py-0.5 rounded">
                        Hidden
                      </span>
                    )}
                    {reward.recurrenceType && reward.recurrenceType !== 'none' && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded flex items-center gap-1">
                        <RefreshCw size={12} /> {reward.recurrenceType.charAt(0).toUpperCase() + reward.recurrenceType.slice(1)}
                      </span>
                    )}
                    {/* XP Multiplier badge */}
                    {reward.rewardType === 'xp_multiplier' && (
                      <span className="text-xs bg-gradient-to-r from-amber-500/30 to-orange-500/30 text-amber-300 px-2 py-0.5 rounded flex items-center gap-1 border border-amber-500/40">
                        <Zap size={12} /> {parseFloat(reward.multiplierValue || 1)}X XP
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 truncate">{reward.description}</p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Target size={12} />
                      {TARGET_TYPES.find(t => t.value === reward.targetType)?.label}
                    </span>
                    <span className="flex items-center gap-1">
                      <Award size={12} />
                      {reward.totalRedemptions || 0} redeemed
                    </span>
                    <span>
                      {USAGE_TYPES.find(u => u.value === reward.usageType)?.label}
                      {reward.usageType === 'multiple' && reward.usageLimit && ` (${reward.usageLimit}x)`}
                    </span>
                    {reward.expiresAt ? (
                      <span className={`flex items-center gap-1 ${new Date(reward.expiresAt) < new Date() ? 'text-red-400' : ''}`} title="Fixed expiration date">
                        <Clock size={12} /> Exp {new Date(reward.expiresAt).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    ) : reward.expiresAfterDays ? (
                      <span className="flex items-center gap-1" title="No fixed date — expires this many days after each player is assigned">
                        <Clock size={12} /> {reward.expiresAfterDays}d after assign
                      </span>
                    ) : null}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setExpandedId(expandedId === reward.id ? null : reward.id)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
                  >
                    {expandedId === reward.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  <button
                    onClick={() => handleToggleHidden(reward)}
                    className={`p-2 rounded-lg ${
                      reward.isHidden 
                        ? 'text-gray-500 hover:text-white hover:bg-gray-700' 
                        : 'text-green-400 hover:bg-green-500/10'
                    }`}
                    title={reward.isHidden ? 'Show reward' : 'Hide reward'}
                  >
                    {reward.isHidden ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <button
                    onClick={() => handleToggleActive(reward)}
                    className={`p-2 rounded-lg ${
                      reward.isActive 
                        ? 'text-green-400 hover:bg-green-500/10' 
                        : 'text-gray-500 hover:text-white hover:bg-gray-700'
                    }`}
                    title={reward.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {reward.isActive ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                  </button>
                  <button
                    onClick={() => openNotifyRecipients(reward)}
                    className="p-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg"
                    title="Notify recipients of this reward"
                  >
                    <Bell size={18} />
                  </button>
                  <button
                    onClick={() => openEditModal(reward)}
                    className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(reward)}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedId === reward.id && (
                <div className="border-t border-gray-700 p-4 bg-gray-800/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    {/* Redemption Instructions */}
                    {reward.redemptionInstructions && (
                      <div className="col-span-full">
                        <label className="text-gray-500 block mb-1">Redemption Instructions</label>
                        <p className="text-gray-300">{reward.redemptionInstructions}</p>
                      </div>
                    )}

                    {/* Schedule */}
                    <div>
                      <label className="text-gray-500 block mb-1">Visibility Schedule</label>
                      <div className="text-gray-300">
                        {reward.visibleFrom && (
                          <div className="flex items-center gap-1">
                            <Calendar size={14} /> From: {new Date(reward.visibleFrom).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                        )}
                        {reward.visibleUntil && (
                          <div className="flex items-center gap-1">
                            <Calendar size={14} /> Until: {new Date(reward.visibleUntil).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </div>
                        )}
                        {!reward.visibleFrom && !reward.visibleUntil && (
                          <span className="text-gray-500">Always visible</span>
                        )}
                      </div>
                    </div>

                    {/* Expiration */}
                    <div>
                      <label className="text-gray-500 block mb-1">Expiration</label>
                      <div className="text-gray-300">
                        {reward.expiresAt && reward.expiresAfterDays ? (
                          <div className="flex items-center gap-1">
                            <Clock size={14} /> {new Date(reward.expiresAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                            <span className="text-gray-500">&nbsp;or {reward.expiresAfterDays}d after assignment (whichever comes first)</span>
                          </div>
                        ) : reward.expiresAt ? (
                          <div className="flex items-center gap-1">
                            <Clock size={14} /> {new Date(reward.expiresAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                            <span className="text-gray-500">&nbsp;(fixed date)</span>
                          </div>
                        ) : reward.expiresAfterDays ? (
                          <div className="flex items-center gap-1">
                            <Clock size={14} /> {reward.expiresAfterDays} days after each assignment
                            <span className="text-gray-500">&nbsp;(no fixed date)</span>
                          </div>
                        ) : (
                          <span className="text-gray-500">Never expires</span>
                        )}
                      </div>
                    </div>

                    {/* Trigger */}
                    <div>
                      <label className="text-gray-500 block mb-1">Trigger Condition</label>
                      <div className="text-gray-300">
                        {reward.triggerType === 'referral_made' ? (
                          <span className="text-blue-400">
                            Referral Made (Referrer)
                            {reward.requirePlayerClubLink && <span className="text-xs ml-1">(requires PC link)</span>}
                          </span>
                        ) : reward.triggerType === 'referral_session' ? (
                          <span className="text-cyan-400">
                            Referral Played Session (Referrer)
                            {reward.requirePlayerClubLink && <span className="text-xs ml-1">(requires PC link)</span>}
                          </span>
                        ) : reward.triggerType === 'referral_received' ? (
                          <span className="text-green-400">
                            Referral Received (New User)
                            {reward.requirePlayerClubLink && <span className="text-xs ml-1">(requires PC link)</span>}
                          </span>
                        ) : reward.triggerType === 'tier_achieved' ? (
                          <span className="text-purple-400">
                            Tier Achieved: {getTierLabel(reward.triggerTier)}
                          </span>
                        ) : reward.triggerType === 'tier_current' ? (
                          <span className="text-purple-400">
                            Current Tier: {getTierLabel(reward.triggerTier)}+
                          </span>
                        ) : reward.triggerType === 'level_achieved' ? (
                          <span className="text-indigo-400">
                            Level Achieved: {reward.triggerLevel}
                          </span>
                        ) : reward.triggerType === 'level_current' ? (
                          <span className="text-indigo-400">
                            Current Level: {reward.triggerLevel}+
                          </span>
                        ) : reward.triggerType === 'tier_renewed' ? (
                          <span className="text-purple-400">Tier Renewed</span>
                        ) : reward.triggerType === 'achievement_unlocked' ? (
                          <span className="text-yellow-400">
                            Achievement: {reward.triggerAchievementId}
                          </span>
                        ) : reward.triggerType === 'signup' ? (
                          <span className="text-green-400">On Signup</span>
                        ) : reward.triggerType === 'player_club_linked' ? (
                          <span className="text-green-400">Player's Club Linked</span>
                        ) : reward.triggerType === 'birthday' ? (
                          <span className="text-pink-400">Birthday</span>
                        ) : reward.triggerType === 'first_session' ? (
                          <span className="text-cyan-400">First Session</span>
                        ) : reward.triggerType === 'review_confirmed' ? (
                          <span className="text-orange-400">Google Review Confirmed</span>
                        ) : reward.triggerType !== 'none' && reward.triggerValue ? (
                          <span>
                            {reward.triggerValue} {reward.triggerType}
                          </span>
                        ) : (
                          <span className="text-gray-500">Manual assignment</span>
                        )}
                      </div>
                    </div>

                    {/* Recurrence */}
                    {reward.recurrenceType && reward.recurrenceType !== 'none' && (
                      <div>
                        <label className="text-gray-500 block mb-1">Recurrence</label>
                        <div className="text-gray-300">
                          <div className="flex items-center gap-1 text-amber-400">
                            <RefreshCw size={14} /> 
                            {reward.recurrenceType.charAt(0).toUpperCase() + reward.recurrenceType.slice(1)}
                            {reward.recurrenceType === 'weekly' && reward.recurrenceDayOfWeek !== null && (
                              <span className="text-gray-400 ml-1">
                                on {DAY_OF_WEEK_OPTIONS.find(d => d.value === reward.recurrenceDayOfWeek)?.label}
                              </span>
                            )}
                            {(reward.recurrenceType === 'monthly' || reward.recurrenceType === 'yearly') && reward.recurrenceDayOfMonth && (
                              <span className="text-gray-400 ml-1">on day {reward.recurrenceDayOfMonth}</span>
                            )}
                          </div>
                          {reward.recurrenceEndDate && (
                            <div className="text-xs text-gray-500 mt-1">
                              Ends: {new Date(reward.recurrenceEndDate).toLocaleDateString()}
                            </div>
                          )}
                          {reward.lastRecurrenceAt && (
                            <div className="text-xs text-gray-500 mt-1">
                              Last run: {new Date(reward.lastRecurrenceAt).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Assignments */}
                    {reward.assignments && reward.assignments.length > 0 && (
                      <div className="col-span-full">
                        <label className="text-gray-500 block mb-1">Assigned To</label>
                        <div className="flex flex-wrap gap-2">
                          {reward.assignments.map((a, i) => (
                            <span key={i} className="bg-gray-700 text-gray-300 px-2 py-1 rounded text-xs">
                              {a.user ? `${a.user.firstName} ${a.user.lastName}` : a.group?.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Spanish Content */}
                    {(reward.titleEs || reward.descriptionEs) && (
                      <div className="col-span-full border-t border-gray-700 pt-3 mt-2">
                        <label className="text-gray-500 block mb-1">Spanish Content</label>
                        {reward.titleEs && <p className="text-gray-300">Title: {reward.titleEs}</p>}
                        {reward.descriptionEs && <p className="text-gray-400 text-xs">{reward.descriptionEs}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingReward ? 'Edit Reward' : 'Create Reward'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Title *</label>
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="Free Appetizer"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Title (Spanish)</label>
                  <input
                    value={form.titleEs}
                    onChange={(e) => setForm({ ...form, titleEs: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder="Aperitivo Gratis"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  rows={2}
                  placeholder="Enjoy a complimentary appetizer at the restaurant"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Description (Spanish)</label>
                <textarea
                  value={form.descriptionEs}
                  onChange={(e) => setForm({ ...form, descriptionEs: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  rows={2}
                  placeholder="Disfruta de un aperitivo de cortesía"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Icon</label>
                  <select
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {ICONS.map(i => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Select category...</option>
                    {CATEGORIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Value Type</label>
                  <select
                    value={form.rewardValueType}
                    onChange={(e) => setForm({ ...form, rewardValueType: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    {VALUE_TYPES.map(v => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">
                    Value ({form.rewardValueType === 'percent' ? '%' : '$'})
                  </label>
                  <input
                    type="number"
                    step={form.rewardValueType === 'percent' ? '1' : '0.01'}
                    min="0"
                    max={form.rewardValueType === 'percent' ? '100' : undefined}
                    value={form.rewardValue}
                    onChange={(e) => setForm({ ...form, rewardValue: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    placeholder={form.rewardValueType === 'percent' ? '10' : '10.00'}
                  />
                </div>
              </div>

              {/* Reward Type Selection */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Zap size={18} className="text-amber-500" />
                  Reward Type
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Type</label>
                    <select
                      value={form.rewardType}
                      onChange={(e) => setForm({ ...form, rewardType: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      {REWARD_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* XP Multiplier Settings */}
                {form.rewardType === 'xp_multiplier' && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                      <TrendingUp size={16} />
                      XP Multiplier Configuration
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Multiplier Value *</label>
                        <input
                          type="number"
                          step="0.1"
                          min="1.1"
                          max="10"
                          value={form.multiplierValue}
                          onChange={(e) => setForm({ ...form, multiplierValue: e.target.value })}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          placeholder="2"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          e.g., 2 = 2X (double XP), 3 = 3X (triple XP)
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Applies To</label>
                        <select
                          value={form.multiplierAppliesTo?.[0] || 'all'}
                          onChange={(e) => setForm({ ...form, multiplierAppliesTo: [e.target.value] })}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        >
                          {MULTIPLIER_APPLIES_TO_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {form.multiplierValue && (
                      <div className="mt-3 text-sm text-gray-400">
                        Preview: <span className="text-amber-400 font-bold text-lg">
                          {parseFloat(form.multiplierValue || 1)}X
                        </span> XP for all eligible users
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Use <strong>Visibility Schedule</strong> below to control when this multiplier is active. 
                      The multiplier applies automatically to all eligible users during the visible period.
                    </p>
                  </div>
                )}
              </div>

              {/* Redemption */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3">Redemption Settings</h3>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Redemption Instructions</label>
                  <textarea
                    value={form.redemptionInstructions}
                    onChange={(e) => setForm({ ...form, redemptionInstructions: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    rows={2}
                    placeholder="Present this screen to the cashier to redeem"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm text-gray-400 mb-1">Instructions (Spanish)</label>
                  <textarea
                    value={form.redemptionInstructionsEs}
                    onChange={(e) => setForm({ ...form, redemptionInstructionsEs: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    rows={2}
                    placeholder="Presente esta pantalla al cajero para canjear"
                  />
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requiresStaffAuth}
                      onChange={(e) => setForm({ ...form, requiresStaffAuth: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Requires Staff Authentication</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.excludeStaff}
                      onChange={(e) => setForm({ ...form, excludeStaff: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Exclude Staff Members</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.excludeAdmins}
                      onChange={(e) => setForm({ ...form, excludeAdmins: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-gray-300">Exclude Admin Members</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.existingMembersOnly}
                      onChange={(e) => setForm({ ...form, existingMembersOnly: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Existing members only (hide from accounts created after this offer)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.excludeList}
                      onChange={(e) => setForm({ ...form, excludeList: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-red-500 focus:ring-red-500"
                    />
                    <span className="text-gray-300">Exclude list from reward (skip everyone on the reward exclusion list)</span>
                  </label>
                </div>
              </div>

              {/* Usage */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3">Usage Limits</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Usage Type</label>
                    <select
                      value={form.usageType}
                      onChange={(e) => setForm({ ...form, usageType: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      {USAGE_TYPES.map(u => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  </div>
                  {form.usageType === 'multiple' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Usage Limit</label>
                      <input
                        type="number"
                        value={form.usageLimit}
                        onChange={(e) => setForm({ ...form, usageLimit: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        placeholder="5"
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.dailyExclusive}
                      onChange={(e) => setForm({ ...form, dailyExclusive: e.target.checked })}
                      className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">
                      One per day across toggled rewards
                      <span className="block text-xs text-gray-500">
                        Player can redeem only ONE reward with this toggle per day (6 AM Pacific reset).
                        Redeeming any toggled reward locks the others until the next day.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Targeting */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3">Targeting</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Target Audience</label>
                    <select
                      value={form.targetType}
                      onChange={(e) => {
                        setForm({ ...form, targetType: e.target.value });
                        if (e.target.value !== 'users') {
                          setSelectedUsers([]);
                          setUserSearchQuery('');
                        }
                        if (e.target.value !== 'activity') {
                          setActivityPreview(null);
                        }
                      }}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      {TARGET_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Specific Users Selection */}
                  {form.targetType === 'users' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Users</label>
                      {/* Selected Users Tags */}
                      {selectedUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {selectedUsers.map(user => (
                            <span key={user.id} className="flex items-center gap-1 bg-amber-500/20 text-amber-400 px-2 py-1 rounded text-sm">
                              {user.firstName} {user.lastName || user.email}
                              <button
                                type="button"
                                onClick={() => removeSelectedUser(user.id)}
                                className="hover:text-amber-200"
                              >
                                <X size={14} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {/* User Search */}
                      <div className="relative">
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          placeholder="Search users by email or name..."
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        />
                        {searchingUsers && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-500 border-t-transparent"></div>
                          </div>
                        )}
                      </div>
                      {/* Search Results */}
                      {userSearchResults.length > 0 && (
                        <div className="mt-2 bg-gray-700 border border-gray-600 rounded-lg max-h-48 overflow-y-auto">
                          {userSearchResults.map(user => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => toggleUserSelection(user)}
                              disabled={selectedUsers.find(u => u.id === user.id)}
                              className={`w-full text-left px-3 py-2 hover:bg-gray-600 flex items-center justify-between ${
                                selectedUsers.find(u => u.id === user.id) ? 'opacity-50' : ''
                              }`}
                            >
                              <div>
                                <span className="text-white">{user.firstName} {user.lastName}</span>
                                <span className="text-gray-400 text-sm ml-2">{user.email}</span>
                              </div>
                              {selectedUsers.find(u => u.id === user.id) && (
                                <CheckCircle size={16} className="text-green-400" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                      {selectedUsers.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">Type to search and select users</p>
                      )}
                    </div>
                  )}

                  {/* User Groups Selection */}
                  {form.targetType === 'groups' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Groups</label>
                      <div className="bg-gray-700 border border-gray-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                        {userGroups.map(group => (
                          <label key={group.id} className="flex items-center gap-2 p-1 hover:bg-gray-600 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.assignedGroupIds.includes(group.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setForm({ ...form, assignedGroupIds: [...form.assignedGroupIds, group.id] });
                                } else {
                                  setForm({ ...form, assignedGroupIds: form.assignedGroupIds.filter(id => id !== group.id) });
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                            />
                            <span className="text-gray-300 text-sm">{group.name}</span>
                          </label>
                        ))}
                        {userGroups.length === 0 && (
                          <p className="text-gray-500 text-sm">No groups available</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recent Play (Activity Filter) */}
                  {form.targetType === 'activity' && (
                    <div className="bg-gray-700/40 border border-gray-600 rounded-lg p-3 space-y-3">
                      <p className="text-xs text-gray-400">
                        Resolves on save to the list of players whose total table time across
                        the selected game types meets the threshold. Matched players are assigned
                        as individual recipients{editingReward ? ' (merged with any users already on this reward)' : ''}.
                      </p>

                      {/* Game type checkboxes */}
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Game Types</label>
                        <div className="flex gap-3">
                          {[
                            { key: 'poker', label: 'Poker' },
                            { key: 'cal_games', label: 'Cal Games' },
                          ].map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-2 text-gray-200 text-sm">
                              <input
                                type="checkbox"
                                checked={(form.activityGames || []).includes(key)}
                                onChange={(e) => {
                                  const current = new Set(form.activityGames || []);
                                  if (e.target.checked) current.add(key);
                                  else current.delete(key);
                                  setForm({ ...form, activityGames: Array.from(current) });
                                  setActivityPreview(null);
                                }}
                                className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Match by</label>
                          <select
                            value={form.activityMatchMode || 'hours'}
                            onChange={(e) => { setForm({ ...form, activityMatchMode: e.target.value }); setActivityPreview(null); }}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          >
                            <option value="hours">Hours in selected games</option>
                            <option value="percent">% of play mix</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Based on</label>
                          <select
                            value={form.activityScope || 'recent'}
                            onChange={(e) => { setForm({ ...form, activityScope: e.target.value }); setActivityPreview(null); }}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          >
                            <option value="recent">Recent play</option>
                            <option value="lifetime">Lifetime play</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">
                            {form.activityMatchMode === 'percent' ? 'Minimum Total Hours' : 'Minimum Hours'}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={form.activityMinHours}
                            onChange={(e) => {
                              setForm({ ...form, activityMinHours: e.target.value });
                              setActivityPreview(null);
                            }}
                            placeholder="e.g. 5"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          />
                        </div>
                        {form.activityScope !== 'lifetime' && (
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">In Last N Days</label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={form.activityDaysWindow}
                              onChange={(e) => {
                                setForm({ ...form, activityDaysWindow: e.target.value });
                                setActivityPreview(null);
                              }}
                              placeholder="e.g. 30"
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                            />
                          </div>
                        )}
                        {form.activityMatchMode === 'percent' && (
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Min % in selected games</label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              step="1"
                              value={form.activityMinPercent}
                              onChange={(e) => {
                                setForm({ ...form, activityMinPercent: e.target.value });
                                setActivityPreview(null);
                              }}
                              placeholder="e.g. 60"
                              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                            />
                          </div>
                        )}
                      </div>
                      {form.activityMatchMode === 'percent' && (
                        <p className="text-xs text-gray-500">
                          Targets players with at least the Minimum Total Hours of {form.activityScope === 'lifetime' ? 'lifetime' : 'recent'} play,
                          where the selected game{(form.activityGames || []).length > 1 ? 's make' : ' makes'} up at least that % of their total play time.
                        </p>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={previewFormActivityFilter}
                          disabled={activityPreviewLoading}
                          className="text-sm bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 px-3 py-1.5 rounded disabled:opacity-50"
                        >
                          {activityPreviewLoading ? (
                            <span className="flex items-center gap-2">
                              <span className="inline-block w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></span>
                              Checking...
                            </span>
                          ) : 'Preview match count'}
                        </button>
                        {activityPreview && (
                          <span className="text-sm text-gray-300">
                            <span className="text-amber-400 font-medium">{activityPreview.matched}</span> player{activityPreview.matched !== 1 ? 's' : ''} match
                            {activityPreview.candidates > activityPreview.matched && (
                              <span className="text-gray-500"> (of {activityPreview.candidates} active in window)</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Triggers */}
              <div className="border-t border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">Activity Trigger (Optional)</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.useMultipleTriggers}
                      onChange={(e) => setForm({ 
                        ...form, 
                        useMultipleTriggers: e.target.checked,
                        triggerConditions: e.target.checked ? form.triggerConditions : [],
                      })}
                      className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                    />
                    <span className="text-sm text-gray-400">Use Multiple Conditions</span>
                  </label>
                </div>
                
                {/* Multiple Triggers UI */}
                {form.useMultipleTriggers ? (
                  <div className="space-y-4">
                    {/* Logic Selector */}
                    <div className="flex items-center gap-4 p-3 bg-gray-700/50 rounded-lg">
                      <span className="text-sm text-gray-400">Match:</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="triggerLogic"
                          checked={form.triggerLogic === 'AND'}
                          onChange={() => setForm({ ...form, triggerLogic: 'AND' })}
                          className="w-4 h-4 border-gray-500 bg-gray-600 text-amber-500"
                        />
                        <span className="text-white">ALL conditions (AND)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="triggerLogic"
                          checked={form.triggerLogic === 'OR'}
                          onChange={() => setForm({ ...form, triggerLogic: 'OR' })}
                          className="w-4 h-4 border-gray-500 bg-gray-600 text-amber-500"
                        />
                        <span className="text-white">ANY condition (OR)</span>
                      </label>
                    </div>
                    
                    {/* Existing Conditions */}
                    {form.triggerConditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-gray-700 rounded-lg">
                        <div className="flex-1">
                          <span className="text-white">
                            {TRIGGER_TYPES.find(t => t.value === condition.type)?.label || condition.type}
                            {condition.tier && ` - ${condition.tier}`}
                            {condition.level && ` - Level ${condition.level}`}
                            {condition.value && ` - ${condition.value}`}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newConditions = [...form.triggerConditions];
                            newConditions.splice(index, 1);
                            setForm({ ...form, triggerConditions: newConditions });
                          }}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                    
                    {/* Add Condition Form */}
                    <div className="p-3 border border-dashed border-gray-600 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select
                          id="newConditionType"
                          className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          defaultValue=""
                        >
                          <option value="">Select trigger type...</option>
                          <optgroup label="Tier-Based">
                            <option value="tier_current">Current Tier (Ongoing)</option>
                            <option value="level_current">Current Level (Ongoing)</option>
                          </optgroup>
                          <optgroup label="Activity-Based">
                            {TRIGGER_TYPES.filter(t => t.category === 'activity').map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Lifecycle">
                            {TRIGGER_TYPES.filter(t => t.category === 'lifecycle').map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </optgroup>
                        </select>
                        
                        <select
                          id="newConditionTier"
                          className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                          defaultValue=""
                        >
                          <option value="">Select tier (if applicable)...</option>
                          {tierOptions.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        
                        <input
                          type="number"
                          id="newConditionValue"
                          placeholder="Value (if applicable)"
                          className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const typeEl = document.getElementById('newConditionType');
                          const tierEl = document.getElementById('newConditionTier');
                          const valueEl = document.getElementById('newConditionValue');
                          
                          if (!typeEl.value) {
                            toast.error('Please select a trigger type');
                            return;
                          }
                          
                          const newCondition = { type: typeEl.value };
                          if (tierEl.value) newCondition.tier = tierEl.value;
                          if (valueEl.value) newCondition.value = parseInt(valueEl.value);
                          
                          // Extract level from tier selector for level_current
                          if (typeEl.value === 'level_current' && valueEl.value) {
                            newCondition.level = parseInt(valueEl.value);
                          }
                          
                          setForm({ 
                            ...form, 
                            triggerConditions: [...form.triggerConditions, newCondition] 
                          });
                          
                          // Reset inputs
                          typeEl.value = '';
                          tierEl.value = '';
                          valueEl.value = '';
                        }}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400"
                      >
                        <Plus size={16} /> Add Condition
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Single Trigger UI (Original) */
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Trigger Type</label>
                    <select
                      value={form.triggerType}
                      onChange={(e) => setForm({ ...form, triggerType: e.target.value, triggerValue: '', triggerTier: '', triggerLevel: '', triggerAchievementId: '' })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      <optgroup label="Manual">
                        <option value="none">No Trigger (Manual)</option>
                      </optgroup>
                      <optgroup label="Activity-Based">
                        {TRIGGER_TYPES.filter(t => t.category === 'activity').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Tier-Based">
                        {TRIGGER_TYPES.filter(t => t.category === 'tier').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Achievement">
                        {TRIGGER_TYPES.filter(t => t.category === 'achievement').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Account Lifecycle">
                        {TRIGGER_TYPES.filter(t => t.category === 'lifecycle').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                      <optgroup label="Referral">
                        {TRIGGER_TYPES.filter(t => t.category === 'referral').map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  
                  {/* Trigger Value (for activity triggers) */}
                  {getTriggerConfig().needsValue && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Trigger Value</label>
                      <input
                        type="number"
                        value={form.triggerValue}
                        onChange={(e) => setForm({ ...form, triggerValue: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                        placeholder={form.triggerType === 'hours' ? '10' : form.triggerType === 'points' ? '1000' : '5'}
                      />
                    </div>
                  )}
                  
                  {/* Tier Selector */}
                  {getTriggerConfig().needsTier && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Tier</label>
                      <select
                        value={form.triggerTier}
                        onChange={(e) => setForm({ ...form, triggerTier: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="">Select tier...</option>
                        {tierOptions.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Level Selector */}
                  {getTriggerConfig().needsLevel && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Level</label>
                      <select
                        value={form.triggerLevel}
                        onChange={(e) => setForm({ ...form, triggerLevel: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="">Select level...</option>
                        {LEVEL_OPTIONS.map(l => (
                          <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Achievement Selector */}
                  {getTriggerConfig().needsAchievement && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Select Achievement</label>
                      <select
                        value={form.triggerAchievementId}
                        onChange={(e) => setForm({ ...form, triggerAchievementId: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="">Select achievement...</option>
                        {achievements.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                )}
                
                {/* Trigger Info Messages - Only shown for single trigger mode */}
                {!form.useMultipleTriggers && form.triggerType === 'tier_achieved' && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      <strong>Tier Achieved:</strong> This reward will be given when a user achieves the selected tier status.
                    </p>
                  </div>
                )}
                {form.triggerType === 'level_achieved' && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      <strong>Level Achieved:</strong> This reward will be given when a user reaches the selected level.
                    </p>
                  </div>
                )}
                {form.triggerType === 'achievement_unlocked' && (
                  <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-300">
                      <strong>Achievement Unlocked:</strong> This reward will be given when a user unlocks the selected achievement.
                    </p>
                  </div>
                )}
                {form.triggerType === 'signup' && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-300">
                      <strong>On Signup:</strong> This reward will be automatically given to all new users when they create an account.
                    </p>
                  </div>
                )}
                {form.triggerType === 'player_club_linked' && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      <strong>Player's Club Linked:</strong> This reward will be given when a user links their Player's Club card.
                    </p>
                  </div>
                )}
                {form.triggerType === 'birthday' && (
                  <div className="mt-3 p-3 bg-pink-500/10 border border-pink-500/30 rounded-lg">
                    <p className="text-sm text-pink-300">
                      <strong>Birthday:</strong> This reward will be given to users on their birthday (requires date of birth in profile).
                    </p>
                  </div>
                )}
                {form.triggerType === 'first_session' && (
                  <div className="mt-3 p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
                    <p className="text-sm text-teal-300">
                      <strong>First Session:</strong> This reward will be given when a user completes their first play session.
                    </p>
                  </div>
                )}
                {form.triggerType === 'review_confirmed' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Google Review Confirmed:</strong> This reward will be given when a user&apos;s Google review is verified via the Business Profile API. Each user can only earn this once.
                    </p>
                  </div>
                )}
                {form.triggerType === 'referral_made' && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      <strong>Referral Made:</strong> This reward will be given to users when someone signs up using their referral code.
                    </p>
                  </div>
                )}
                {form.triggerType === 'referral_session' && (
                  <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <p className="text-sm text-cyan-300">
                      <strong>Referral Played Session:</strong> This reward will be given to the referrer when their referred user completes any game session. Triggers once per referral.
                    </p>
                  </div>
                )}
                {form.triggerType === 'referral_received' && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-300">
                      <strong>Referral Received:</strong> This reward will be given to new users who sign up using a friend's referral code.
                    </p>
                  </div>
                )}
                {form.triggerType === 'tier_current' && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      <strong>Current Tier (Ongoing):</strong> This reward is visible only to users at the exact selected tier. It appears/disappears dynamically based on current tier status.
                    </p>
                  </div>
                )}
                {form.triggerType === 'level_current' && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      <strong>Current Level (Ongoing):</strong> This reward is visible only to users at the exact selected level. It appears/disappears dynamically based on current level.
                    </p>
                  </div>
                )}
                {form.triggerType === 'tier_renewed' && (
                  <div className="mt-3 p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                    <p className="text-sm text-indigo-300">
                      <strong>Tier Renewed:</strong> This reward will be given when a user maintains their tier status at the start of a new period (monthly/quarterly). A loyalty bonus for returning players!
                    </p>
                  </div>
                )}
                {form.triggerType === 'sessions' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Sessions Played:</strong> This reward will be given when a user reaches the specified number of total sessions played.
                    </p>
                  </div>
                )}
                {form.triggerType === 'hours' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Hours Played:</strong> This reward will be given when a user reaches the specified number of total hours played.
                    </p>
                  </div>
                )}
                {form.triggerType === 'points' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Points Earned:</strong> This reward will be given when a user reaches the specified number of total points earned.
                    </p>
                  </div>
                )}
                {form.triggerType === 'games_played' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Games Played:</strong> This reward will be given when a user plays the specified number of different game types.
                    </p>
                  </div>
                )}
                {form.triggerType === 'consecutive_days' && (
                  <div className="mt-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                    <p className="text-sm text-orange-300">
                      <strong>Consecutive Days:</strong> This reward will be given when a user plays for the specified number of consecutive days.
                    </p>
                  </div>
                )}
              </div>

              {/* Eligibility Conditions */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3">Eligibility Conditions (Optional)</h3>
                <p className="text-xs text-gray-500 mb-3">Users must meet ALL selected conditions to be eligible for this reward</p>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requirePlayerClubLink}
                      onChange={(e) => setForm({ ...form, requirePlayerClubLink: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Require Player's Club Link</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requireVerifiedEmail}
                      onChange={(e) => setForm({ ...form, requireVerifiedEmail: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Require Verified Email</span>
                  </label>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Minimum Tier</label>
                    <select
                      value={form.requireMinTier}
                      onChange={(e) => setForm({ ...form, requireMinTier: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">No minimum</option>
                      {tierOptions.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Minimum Level</label>
                    <input
                      type="number"
                      min="1"
                      max="13"
                      value={form.requireMinLevel}
                      onChange={(e) => setForm({ ...form, requireMinLevel: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Min Sessions</label>
                    <input
                      type="number"
                      min="1"
                      value={form.requireMinSessions}
                      onChange={(e) => setForm({ ...form, requireMinSessions: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      placeholder="e.g., 5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Min Hours</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={form.requireMinHours}
                      onChange={(e) => setForm({ ...form, requireMinHours: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      placeholder="e.g., 10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Min Points</label>
                    <input
                      type="number"
                      min="0"
                      value={form.requireMinPoints}
                      onChange={(e) => setForm({ ...form, requireMinPoints: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      placeholder="e.g., 1000"
                    />
                  </div>
                </div>
              </div>

              {/* Recurrence Settings */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <RefreshCw size={18} className="text-amber-500" />
                  Recurrence Settings
                </h3>
                <p className="text-xs text-gray-500 mb-3">Configure recurring rewards that automatically reassign on a schedule</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Recurrence Type</label>
                    <select
                      value={form.recurrenceType}
                      onChange={(e) => setForm({ 
                        ...form, 
                        recurrenceType: e.target.value,
                        recurrenceDayOfWeek: '',
                        recurrenceDayOfMonth: '',
                      })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      {RECURRENCE_TYPES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Day of Week for Weekly */}
                  {form.recurrenceType === 'weekly' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Day of Week</label>
                      <select
                        value={form.recurrenceDayOfWeek}
                        onChange={(e) => setForm({ ...form, recurrenceDayOfWeek: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      >
                        <option value="">Any day</option>
                        {DAY_OF_WEEK_OPTIONS.map(d => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {/* Day of Month for Monthly/Yearly */}
                  {(form.recurrenceType === 'monthly' || form.recurrenceType === 'yearly') && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Day of Month</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={form.recurrenceDayOfMonth}
                        onChange={(e) => setForm({ ...form, recurrenceDayOfMonth: e.target.value })}
                        placeholder="e.g., 1 for first of month"
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  )}
                  
                  {/* Recurrence End Date */}
                  {form.recurrenceType !== 'none' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">End Date (Optional)</label>
                      <input
                        type="datetime-local"
                        value={form.recurrenceEndDate}
                        onChange={(e) => setForm({ ...form, recurrenceEndDate: e.target.value })}
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for no end date</p>
                    </div>
                  )}
                </div>
                
                {/* Recurrence Info Messages */}
                {form.recurrenceType === 'daily' && (
                  <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-sm text-amber-300">
                      <strong>Daily:</strong> This reward will be available fresh every day. Users who redeemed yesterday can redeem again today.
                    </p>
                  </div>
                )}
                {form.recurrenceType === 'weekly' && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      <strong>Weekly:</strong> This reward resets once per week. 
                      {form.recurrenceDayOfWeek !== '' 
                        ? ` Specifically on ${DAY_OF_WEEK_OPTIONS.find(d => d.value === parseInt(form.recurrenceDayOfWeek))?.label || 'selected day'}.`
                        : ' Will be processed any day users are eligible.'}
                    </p>
                  </div>
                )}
                {form.recurrenceType === 'monthly' && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300">
                      <strong>Monthly:</strong> This reward resets once per month.
                      {form.recurrenceDayOfMonth 
                        ? ` Specifically on day ${form.recurrenceDayOfMonth} of each month.`
                        : ' Will be processed on any day users are eligible.'}
                    </p>
                  </div>
                )}
                {form.recurrenceType === 'yearly' && (
                  <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-300">
                      <strong>Yearly:</strong> This reward resets once per year. Great for annual birthday rewards or anniversary bonuses.
                    </p>
                  </div>
                )}
              </div>

              {/* Schedule */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-1">Visibility Schedule</h3>
                <p className="text-xs text-gray-500 mb-3">Times are in your local timezone</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Visible From</label>
                    <input
                      type="datetime-local"
                      value={form.visibleFrom}
                      onChange={(e) => setForm({ ...form, visibleFrom: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Visible Until</label>
                    <input
                      type="datetime-local"
                      value={form.visibleUntil}
                      onChange={(e) => setForm({ ...form, visibleUntil: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
                
                {/* Daily Time Window */}
                <h4 className="text-white font-medium mb-2 mt-4">Daily Active Time Window</h4>
                <p className="text-xs text-gray-500 mb-3">Optionally restrict this reward to only be visible/earnable during certain hours each day (uses casino local time)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Active From (time)</label>
                    <input
                      type="time"
                      value={form.scheduleStartTime}
                      onChange={(e) => setForm({ ...form, scheduleStartTime: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Active Until (time)</label>
                    <input
                      type="time"
                      value={form.scheduleEndTime}
                      onChange={(e) => setForm({ ...form, scheduleEndTime: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>
                {(form.scheduleStartTime || form.scheduleEndTime) && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, scheduleStartTime: '', scheduleEndTime: '' })}
                    className="mt-2 text-xs text-red-400 hover:text-red-300"
                  >
                    Clear time window
                  </button>
                )}

                <h4 className="text-white font-medium mb-3 mt-4">Expiration</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Expires At (specific date)</label>
                    <input
                      type="datetime-local"
                      value={form.expiresAt}
                      onChange={(e) => setForm({ ...form, expiresAt: e.target.value, expiresAfterDays: '' })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      disabled={form.expiresAfterDays !== ''}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Or expires after (days from assignment)</label>
                    <input
                      type="number"
                      min="1"
                      value={form.expiresAfterDays}
                      onChange={(e) => setForm({ ...form, expiresAfterDays: e.target.value, expiresAt: '' })}
                      placeholder="e.g., 30"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      disabled={form.expiresAt !== ''}
                    />
                    <p className="text-xs text-gray-500 mt-1">For referral rewards: expires X days after user receives it</p>
                  </div>
                </div>
              </div>

              {/* Promo Code */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-1">Promo Code (optional)</h3>
                <p className="text-xs text-gray-500 mb-3">
                  When set, this reward is hidden from players until they enter the code at signup or on the Rewards screen. Per-player single-redemption is enforced automatically. Leave blank for no code.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Promo Code</label>
                    <input
                      type="text"
                      value={form.promoCode}
                      onChange={(e) => setForm({ ...form, promoCode: e.target.value.toUpperCase() })}
                      placeholder="e.g., WELCOME50"
                      maxLength={40}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white font-mono tracking-widest uppercase"
                    />
                    <p className="text-xs text-gray-500 mt-1">Case-insensitive. Stored uppercased.</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Max total redemptions (optional)</label>
                    <input
                      type="number"
                      min="1"
                      value={form.promoCodeMaxUses}
                      onChange={(e) => setForm({ ...form, promoCodeMaxUses: e.target.value })}
                      placeholder="e.g., 100"
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      disabled={!form.promoCode}
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank for uncapped.</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Code active from (optional)</label>
                    <input
                      type="datetime-local"
                      value={form.promoCodeStartsAt}
                      onChange={(e) => setForm({ ...form, promoCodeStartsAt: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      disabled={!form.promoCode}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Code active until (optional)</label>
                    <input
                      type="datetime-local"
                      value={form.promoCodeEndsAt}
                      onChange={(e) => setForm({ ...form, promoCodeEndsAt: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                      disabled={!form.promoCode}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.promoCodeSignupOnly}
                    onChange={(e) => setForm({ ...form, promoCodeSignupOnly: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    disabled={!form.promoCode}
                  />
                  <span className="text-gray-300">Signup only — code can only be redeemed during account creation</span>
                </label>
                {form.promoCode && editingReward && typeof editingReward.promoCodeCurrentUses === 'number' && (
                  <p className="text-xs text-amber-400 mt-3">
                    Redeemed {editingReward.promoCodeCurrentUses}
                    {form.promoCodeMaxUses ? ` / ${form.promoCodeMaxUses}` : ''} time{editingReward.promoCodeCurrentUses === 1 ? '' : 's'} so far.
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-white font-medium mb-3">Status</h3>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Active</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.isHidden}
                      onChange={(e) => setForm({ ...form, isHidden: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-gray-300">Hidden</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-6 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-400"
              >
                <Save size={18} />
                {editingReward ? 'Update Reward' : 'Create Reward'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Deactivate Modal */}
      {showBulkDeactivate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <div className="flex items-center gap-2 text-white font-semibold">
                <Archive className="text-gray-300" size={20} /> Bulk Deactivate Rewards
              </div>
              <button onClick={() => setShowBulkDeactivate(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-400">
                Deactivate older rewards in one step. Recurring, promo-code, and
                not-yet-started scheduled rewards are always left active. Deactivating is
                reversible — you can re-activate any reward from its row.
              </p>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Deactivate rewards older than</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" step="1"
                    value={bulkDays}
                    onChange={(e) => { setBulkDays(e.target.value); setBulkPreview(null); }}
                    className="w-28 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                  <span className="text-gray-300 text-sm">days</span>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-200">
                <input
                  type="checkbox"
                  checked={bulkQuickSendOnly}
                  onChange={(e) => { setBulkQuickSendOnly(e.target.checked); setBulkPreview(null); }}
                  className="w-4 h-4"
                />
                Quick Send rewards only (recommended)
              </label>
              <p className="text-xs text-gray-500">
                {bulkQuickSendOnly
                  ? 'Only individually-sent rewards (Quick Send) are affected — evergreen and triggered rewards are left alone.'
                  : 'ALL rewards older than the cutoff will be deactivated, including evergreen/triggered ones. Use with care.'}
              </p>
              {bulkPreview && (
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-sm text-gray-200">
                  <span className="text-amber-400 font-semibold">{bulkPreview.matched}</span> reward{bulkPreview.matched === 1 ? '' : 's'} match and will be deactivated.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-700">
              <button onClick={() => setShowBulkDeactivate(false)} disabled={bulkBusy} className="px-4 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">Cancel</button>
              <button onClick={previewBulkDeactivate} disabled={bulkBusy} className="px-4 py-2 rounded-lg bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600 disabled:opacity-50">Preview count</button>
              <button
                onClick={runBulkDeactivate}
                disabled={bulkBusy || !bulkPreview || bulkPreview.matched === 0}
                className="px-4 py-2 rounded-lg bg-red-700 text-white font-semibold hover:bg-red-600 disabled:opacity-50"
                title={!bulkPreview ? 'Preview the count first' : ''}
              >
                {bulkBusy ? 'Working…' : bulkPreview ? `Deactivate ${bulkPreview.matched}` : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending-recipient acknowledgment — staff must confirm the reward was not delivered */}
      {qsAck && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-amber-600/60">
            <div className="p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={22} />
                <div>
                  <h3 className="text-lg font-bold text-white">Reward saved, not delivered yet</h3>
                  <p className="text-sm text-gray-300 mt-1">
                    {qsAck.assigned > 0 && (
                      <>{qsAck.assigned} player{qsAck.assigned !== 1 ? 's' : ''} received it now. </>
                    )}
                    {qsAck.unmatched.length} member number{qsAck.unmatched.length !== 1 ? 's' : ''}{' '}
                    {qsAck.unmatched.length !== 1 ? 'have' : 'has'} no app account, so{' '}
                    {qsAck.unmatched.length !== 1 ? 'they' : 'that player'} did{' '}
                    <span className="font-semibold text-amber-300">NOT</span> receive "{qsAck.title}".
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    It is saved and will apply automatically once they download the EDH Casino app and
                    link their Player's Club account. Let the member know they need to sign up to claim it.
                  </p>
                </div>
              </div>
              <div className="mt-3 max-h-24 overflow-y-auto font-mono text-xs text-amber-100 bg-gray-900/60 rounded p-2">
                {qsAck.unmatched.join(', ')}
              </div>
              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  onClick={() => { try { navigator.clipboard?.writeText(qsAck.unmatched.join('\n')); toast.success('Copied numbers'); } catch { /* clipboard unavailable */ } }}
                  className="text-xs px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white"
                >
                  Copy numbers
                </button>
                <button
                  onClick={() => { setQsAck(null); setQsUnmatched([]); setQsExcluded([]); setShowQuickSend(false); }}
                  className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm"
                >
                  Got it, they need the app
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Send Modal */}
      {showQuickSend && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col border border-gray-700">
            {/* Header */}
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-2">
                <Zap className="text-emerald-400" size={20} />
                <h2 className="text-lg font-bold text-white">Quick Send Reward</h2>
              </div>
              <button onClick={() => setShowQuickSend(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Reward Title *</label>
                <input
                  type="text"
                  value={quickSendForm.title}
                  onChange={(e) => setQuickSendForm({ ...quickSendForm, title: e.target.value })}
                  placeholder="e.g. $25 Free Play, Bonus Buy-in"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input
                  type="text"
                  value={quickSendForm.description}
                  onChange={(e) => setQuickSendForm({ ...quickSendForm, description: e.target.value })}
                  placeholder="Optional note for players"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                />
              </div>

              {/* Value Row */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm text-gray-400 mb-1">Value</label>
                  <input
                    type="number"
                    value={quickSendForm.rewardValue}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, rewardValue: e.target.value })}
                    placeholder="25"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-sm text-gray-400 mb-1">Type</label>
                  <select
                    value={quickSendForm.rewardValueType}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, rewardValueType: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="dollar">Dollar ($)</option>
                    <option value="percent">Percent (%)</option>
                  </select>
                </div>
                <div className="w-48">
                  <label className="block text-sm text-gray-400 mb-1">Expires In</label>
                  <div className="flex gap-1">
                    <input
                      type="number"
                      value={quickSendForm.expiresAfterDays}
                      onChange={(e) => setQuickSendForm({ ...quickSendForm, expiresAfterDays: e.target.value })}
                      placeholder={quickSendForm.expiresUnit === 'exact' ? '—' : '∞'}
                      disabled={quickSendForm.expiresUnit === 'exact'}
                      className="w-20 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    />
                    <select
                      value={quickSendForm.expiresUnit}
                      onChange={(e) => setQuickSendForm({ ...quickSendForm, expiresUnit: e.target.value })}
                      className="bg-gray-700 border border-gray-600 rounded-lg px-2 py-2 text-white text-sm"
                    >
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                      <option value="exact">on date…</option>
                    </select>
                  </div>
                </div>
              </div>

              {quickSendForm.expiresUnit === 'exact' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Exact expiration date &amp; time</label>
                  <input
                    type="datetime-local"
                    value={quickSendForm.expiresAtExact}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, expiresAtExact: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Hard cutoff — expires for everyone at this exact time (your local timezone).</p>
                </div>
              )}

              {/* Category Row */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Reward Type</label>
                <select
                  value={quickSendForm.category}
                  onChange={(e) => setQuickSendForm({ ...quickSendForm, category: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                >
                  <option value="">Uncategorized</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {quickSendForm.category === 'Match Play' && (
                  <p className="text-xs text-amber-400/80 mt-1">
                    Match Play prompts players to acknowledge the required buy-in and same-visit use when they tap the reward.
                  </p>
                )}
              </div>

              {/* Options Row */}
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quickSendForm.requiresStaffAuth}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, requiresStaffAuth: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                  />
                  <span className="text-gray-300">Require Staff Auth</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quickSendForm.sendNotification}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, sendNotification: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                  />
                  <span className="text-gray-300">Send Push Notification</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={quickSendForm.excludeList}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, excludeList: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-red-500"
                  />
                  <span className="text-gray-300">Exclude list from reward</span>
                </label>
              </div>

              {/* Custom Notification Message */}
              {quickSendForm.sendNotification && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Custom Notification Message</label>
                  <input
                    type="text"
                    value={quickSendForm.notificationMessage}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, notificationMessage: e.target.value })}
                    placeholder={`Default: You've received: ${quickSendForm.title || '...'}`}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              )}

              {/* Schedule */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!quickSendForm.scheduledFor}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, scheduledFor: e.target.checked ? '' : null })}
                    className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-amber-500"
                  />
                  <Clock size={14} className="text-gray-400" />
                  <span className="text-gray-300">Schedule for later</span>
                </label>
              </div>
              {quickSendForm.scheduledFor !== null && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Release Date & Time</label>
                  <input
                    type="datetime-local"
                    value={quickSendForm.scheduledFor}
                    onChange={(e) => setQuickSendForm({ ...quickSendForm, scheduledFor: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  />
                  {quickSendForm.scheduledFor && (
                    <p className="text-xs text-gray-500 mt-1">
                      Reward will become visible and notifications will send at this time.
                    </p>
                  )}
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-gray-700 pt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Recipients</label>
                {/* Recipient Mode Tabs */}
                <div className="flex gap-1 bg-gray-900 rounded-lg p-1 mb-3 flex-wrap">
                  {[
                    { value: 'users', label: 'Search Players', icon: Search },
                    { value: 'tier', label: 'By Tier', icon: Crown },
                    { value: 'groups', label: 'Groups', icon: Users },
                    { value: 'activity', label: 'Recent Play', icon: TrendingUp },
                    { value: 'paste', label: 'Paste IDs', icon: UserPlus },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setQuickSendForm({ ...quickSendForm, recipientMode: value })}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        quickSendForm.recipientMode === value
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      <Icon size={14} />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Search Players */}
                {quickSendForm.recipientMode === 'users' && (
                  <div>
                    <div className="relative">
                      <input
                        type="text"
                        value={qsUserSearch}
                        onChange={(e) => setQsUserSearch(e.target.value)}
                        placeholder="Search by email or name..."
                        className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                      />
                      {qsSearching && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-500 border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    {qsUserResults.length > 0 && (
                      <div className="mt-1 bg-gray-700 border border-gray-600 rounded-lg max-h-32 overflow-y-auto">
                        {qsUserResults.map(user => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => toggleQsUser(user)}
                            className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-600 ${
                              qsSelectedUsers.find(u => u.id === user.id) ? 'bg-emerald-900/30 text-emerald-300' : 'text-gray-300'
                            }`}
                          >
                            {user.firstName} {user.lastName} — {user.email}
                            {user.playerClubMember?.memberNumber && (
                              <span className="text-gray-500 ml-1">#{user.playerClubMember.memberNumber}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Selected users pills */}
                    {qsSelectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {qsSelectedUsers.map(user => (
                          <span key={user.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-900/40 text-emerald-300 rounded-full text-xs">
                            {user.firstName || user.email}
                            <button type="button" onClick={() => setQsSelectedUsers(qsSelectedUsers.filter(u => u.id !== user.id))}>
                              <X size={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* By Tier */}
                {quickSendForm.recipientMode === 'tier' && (
                  <div>
                    <select
                      value={quickSendForm.tierFilter}
                      onChange={(e) => setQuickSendForm({ ...quickSendForm, tierFilter: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                    >
                      <option value="">Select tier...</option>
                      {tierOptions.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    {quickSendForm.tierFilter && (
                      <p className="text-xs text-gray-500 mt-1">
                        All players in the {getTierLabel(quickSendForm.tierFilter)} tier will receive this reward.
                      </p>
                    )}
                  </div>
                )}

                {/* Groups */}
                {quickSendForm.recipientMode === 'groups' && (
                  <div className="bg-gray-700 border border-gray-600 rounded-lg p-2 max-h-32 overflow-y-auto">
                    {userGroups.length > 0 ? userGroups.map(group => (
                      <label key={group.id} className="flex items-center gap-2 p-1 hover:bg-gray-600 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={qsSelectedGroups.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setQsSelectedGroups([...qsSelectedGroups, group.id]);
                            } else {
                              setQsSelectedGroups(qsSelectedGroups.filter(id => id !== group.id));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-emerald-500"
                        />
                        <span className="text-gray-300 text-sm">{group.name}</span>
                      </label>
                    )) : (
                      <p className="text-gray-500 text-sm p-1">No groups available</p>
                    )}
                  </div>
                )}

                {/* By Recent Play (Activity Filter) */}
                {quickSendForm.recipientMode === 'activity' && (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-400">
                      Send to players who played at least <strong>X hours</strong> of the selected games in the last <strong>Y days</strong>.
                    </p>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Games</label>
                      <div className="flex gap-4">
                        {[
                          { key: 'poker', label: 'Poker' },
                          { key: 'cal_games', label: 'Cal Games' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(quickSendForm.activityGames || []).includes(key)}
                              onChange={(e) => {
                                const current = new Set(quickSendForm.activityGames || []);
                                if (e.target.checked) current.add(key);
                                else current.delete(key);
                                setQuickSendForm({ ...quickSendForm, activityGames: Array.from(current) });
                                setQsActivityPreview(null);
                              }}
                              className="w-4 h-4 rounded border-gray-500 bg-gray-600 text-emerald-500"
                            />
                            <span className="text-gray-300 text-sm">{label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Hours are summed (OR) across selected games.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Match by</label>
                        <select
                          value={quickSendForm.activityMatchMode || 'hours'}
                          onChange={(e) => { setQuickSendForm({ ...quickSendForm, activityMatchMode: e.target.value }); setQsActivityPreview(null); }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        >
                          <option value="hours">Hours in selected games</option>
                          <option value="percent">% of play mix</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Based on</label>
                        <select
                          value={quickSendForm.activityScope || 'recent'}
                          onChange={(e) => { setQuickSendForm({ ...quickSendForm, activityScope: e.target.value }); setQsActivityPreview(null); }}
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        >
                          <option value="recent">Recent play</option>
                          <option value="lifetime">Lifetime play</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">
                          {quickSendForm.activityMatchMode === 'percent' ? 'Minimum Total Hours' : 'Minimum Hours'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          value={quickSendForm.activityMinHours}
                          onChange={(e) => {
                            setQuickSendForm({ ...quickSendForm, activityMinHours: e.target.value });
                            setQsActivityPreview(null);
                          }}
                          placeholder="e.g., 10"
                          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      {quickSendForm.activityScope !== 'lifetime' && (
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">In Last N Days</label>
                          <input
                            type="number"
                            min="1"
                            value={quickSendForm.activityDaysWindow}
                            onChange={(e) => {
                              setQuickSendForm({ ...quickSendForm, activityDaysWindow: e.target.value });
                              setQsActivityPreview(null);
                            }}
                            placeholder="e.g., 30"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                        </div>
                      )}
                      {quickSendForm.activityMatchMode === 'percent' && (
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Min % in selected games</label>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            value={quickSendForm.activityMinPercent}
                            onChange={(e) => {
                              setQuickSendForm({ ...quickSendForm, activityMinPercent: e.target.value });
                              setQsActivityPreview(null);
                            }}
                            placeholder="e.g., 60"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={previewActivityFilter}
                        disabled={qsActivityPreviewLoading}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg disabled:opacity-50"
                      >
                        {qsActivityPreviewLoading ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-emerald-400 border-t-transparent" />
                        ) : (
                          <Search size={12} />
                        )}
                        Preview match count
                      </button>
                      {qsActivityPreview && (
                        <p className="text-xs text-emerald-300">
                          {qsActivityPreview.matched} player{qsActivityPreview.matched !== 1 ? 's' : ''} match
                          {qsActivityPreview.candidates > qsActivityPreview.matched && (
                            <span className="text-gray-500"> (of {qsActivityPreview.candidates} active in window)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Paste Member Numbers */}
                {quickSendForm.recipientMode === 'paste' && (
                  <div>
                    <textarea
                      value={qsPastedNumbers}
                      onChange={(e) => setQsPastedNumbers(e.target.value)}
                      placeholder="Paste member numbers, one per line or comma-separated..."
                      rows={4}
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm font-mono"
                    />
                    {qsPastedNumbers.trim() && (
                      <p className="text-xs text-gray-500 mt-1">
                        {qsPastedNumbers.split(/[\n,]+/).map(s => s.trim()).filter(Boolean).length} member number(s) detected
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Unmatched member numbers — surfaced so the send is never silent */}
            {qsUnmatched.length > 0 && (
              <div className="mx-4 mb-2 rounded-lg border border-amber-600/60 bg-amber-900/30 p-3">
                <div className="text-amber-200 text-sm font-semibold">
                  ⚠ {qsUnmatched.length} member number{qsUnmatched.length !== 1 ? 's' : ''} matched no account
                </div>
                <p className="text-xs text-amber-200/80 mt-1">
                  These players did <span className="font-semibold">NOT</span> receive the reward. It will
                  apply automatically if/when they create and link an app account. Check for typos or
                  members who haven’t signed up yet.
                </p>
                <div className="mt-2 max-h-24 overflow-y-auto font-mono text-xs text-amber-100 bg-gray-900/50 rounded p-2">
                  {qsUnmatched.join(', ')}
                </div>
                <button
                  onClick={() => {
                    try { navigator.clipboard?.writeText(qsUnmatched.join('\n')); toast.success('Copied unmatched numbers'); }
                    catch { /* clipboard unavailable */ }
                  }}
                  className="mt-2 text-xs px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white"
                >
                  Copy numbers
                </button>
              </div>
            )}

            {/* Excluded recipients — skipped because they're on the reward exclusion list */}
            {qsExcluded.length > 0 && (
              <div className="mx-4 mb-2 rounded-lg border border-red-600/60 bg-red-900/30 p-3">
                <div className="text-red-200 text-sm font-semibold">
                  ⛔ {qsExcluded.length} recipient{qsExcluded.length !== 1 ? 's' : ''} skipped (on exclusion list)
                </div>
                <p className="text-xs text-red-200/80 mt-1">
                  These members are on the reward exclusion list and did <span className="font-semibold">NOT</span> receive the reward.
                </p>
                <div className="mt-2 max-h-24 overflow-y-auto font-mono text-xs text-red-100 bg-gray-900/50 rounded p-2">
                  {qsExcluded.map((e, i) => e.playerName || e.memberNumber || e.userId || `#${i + 1}`).join(', ')}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end gap-3 rounded-b-xl">
              <button
                onClick={() => { setQsUnmatched([]); setQsExcluded([]); setShowQuickSend(false); }}
                className="px-4 py-2 text-gray-400 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleQuickSend}
                disabled={qsSending}
                className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-500 disabled:opacity-50"
              >
                {qsSending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  quickSendForm.scheduledFor ? <Calendar size={18} /> : <Zap size={18} />
                )}
                {qsSending ? 'Sending...' : (quickSendForm.scheduledFor ? 'Schedule Reward' : 'Send Reward')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notify Recipients Modal ── */}
      {showNotifyRecipients && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Bell size={20} className="text-amber-400" />
                Notify Recipients
              </h3>
              <button
                onClick={closeNotifyRecipients}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Rewards selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rewards to Include
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Recipients of any selected reward will get one combined notification. Duplicates are removed.
                </p>
                <div className="max-h-48 overflow-y-auto border border-gray-700 rounded-lg bg-gray-900/40">
                  {rewards
                    .filter(r => r.id === notifyPrimaryRewardId || notifySelectedRewardIds.includes(r.id) || (r.isActive))
                    .map((r) => {
                      const isSelected = notifySelectedRewardIds.includes(r.id);
                      const isPrimary = r.id === notifyPrimaryRewardId;
                      const perReward = notifyPreview?.perReward?.find(p => p.rewardId === r.id);
                      return (
                        <label
                          key={r.id}
                          className={`flex items-center gap-3 px-3 py-2 border-b border-gray-700/50 last:border-b-0 cursor-pointer hover:bg-gray-700/30 ${
                            isSelected ? 'bg-amber-500/5' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isPrimary}
                            onChange={() => toggleNotifyRewardId(r.id)}
                            className="rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500 disabled:opacity-50"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white truncate flex items-center gap-2">
                              {r.title}
                              {isPrimary && (
                                <span className="text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">
                                  Primary
                                </span>
                              )}
                              {!r.isActive && (
                                <span className="text-xs bg-gray-600 text-gray-300 px-1.5 py-0.5 rounded">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {TARGET_TYPES.find(t => t.value === r.targetType)?.label}
                              {perReward != null && (
                                <span className="ml-2 text-amber-400/80">
                                  · {perReward.userCount} recipient{perReward.userCount === 1 ? '' : 's'}
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                </div>
              </div>

              {/* Recipient count banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm">
                  <Users size={16} className="text-amber-400" />
                  {notifyPreviewLoading ? (
                    <span className="text-gray-400">Checking eligibility for every candidate...</span>
                  ) : notifyPreview ? (
                    <>
                      <span className="text-amber-300 font-medium">
                        {notifyPreview.userCount} fully eligible recipient{notifyPreview.userCount === 1 ? '' : 's'}
                      </span>
                      <span className="text-gray-500">
                        across {notifySelectedRewardIds.length} reward{notifySelectedRewardIds.length === 1 ? '' : 's'}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">Select at least one reward</span>
                  )}
                </div>
                {notifyPreview && notifyPreview.filteredOut > 0 && (
                  <p className="text-xs text-gray-400 mt-1">
                    <span className="text-gray-300">{notifyPreview.filteredOut}</span> of{' '}
                    <span className="text-gray-300">{notifyPreview.candidatePoolSize}</span> candidates filtered out
                    (already redeemed, missing tier/level, unverified, expired, or otherwise ineligible)
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Uses the same eligibility logic as the mobile app — only users who would see the reward as claimable right now are notified.
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notification Title
                </label>
                <input
                  type="text"
                  value={notifyForm.title}
                  onChange={(e) => setNotifyForm({ ...notifyForm, title: e.target.value })}
                  maxLength={100}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none"
                  placeholder="🎁 You have a new reward!"
                />
              </div>

              {/* Body */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Message Body
                </label>
                <textarea
                  value={notifyForm.body}
                  onChange={(e) => setNotifyForm({ ...notifyForm, body: e.target.value })}
                  maxLength={200}
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-amber-500 focus:outline-none resize-none"
                  placeholder="Open the app to redeem your reward."
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {notifyForm.body.length}/200
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-700 sticky bottom-0 bg-gray-800">
              <button
                onClick={closeNotifyRecipients}
                disabled={notifySending}
                className="px-4 py-2 text-gray-400 hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={sendNotifyRecipients}
                disabled={
                  notifySending ||
                  notifyPreviewLoading ||
                  !notifyPreview ||
                  notifyPreview.userCount === 0 ||
                  !notifyForm.title.trim() ||
                  !notifyForm.body.trim()
                }
                className="flex items-center gap-2 px-6 py-2 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-500 disabled:opacity-50"
              >
                {notifySending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <Send size={18} />
                )}
                {notifySending
                  ? 'Sending...'
                  : notifyPreview
                  ? `Send to ${notifyPreview.userCount}`
                  : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
